import { IScene2d, loadTexture } from '@shareuc/pixi';
import { IProduct } from '@shareuc/product';
import { loadGLTF } from '@shareuc/three';
import axios from 'axios';
import {
  Application as PixiApplication,
  BLEND_MODES,
  Container as PixiContainer,
  PerspectiveMesh as PixiPerspectiveMesh,
  Rectangle as PixiRectangle,
  Sprite as PixiSprite,
} from 'pixi.js';
import {
  AmbientLight,
  Box3,
  DirectionalLight,
  DoubleSide,
  EquirectangularReflectionMapping,
  FrontSide,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  PMREMGenerator,
  PointLight,
  RepeatWrapping,
  Scene,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  Vector2,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/Addons.js';

interface IPosition3 {
  x: number | string;
  y: number | string;
  z: number | string;
}

interface IHicustomLight {
  position: IPosition3;
  intensity: number | string;
  decay?: number;
  distance?: number;
  closed?: boolean;
  color?: string;
}

interface IHicustomRenderParams {
  position?: IPosition3;
  rotation?: IPosition3;
  zoom?: number | string;
  ambientLightIntensity?: number | string;
  mainPointIntensity?: number | string;
  mainPointPosition?: IPosition3;
  mainPointType?: string;
  aroundPointIntensity?: number | string;
  lights?: IHicustomLight[];
}

interface IHicustomZipfile {
  file_name: string;
  file_url: string;
  ext: string;
}

interface IHicustomUV {
  name: string;
  face_list: Array<{
    view_id: number;
    url?: string;
    // UV 参考尺寸；compositeDesignOnColor 按 params.left/top/scaleX/scaleY 放置设计
    width?: number;
    height?: number;
    params?: IHicustomFaceParams;
  }>;
}

export interface IRenderHicustomOptions {
  // 2D 设计画布的 base64/url。传空字符串表示"无设计"，此时主 uv 保留 zipfiles 默认贴图
  textureSrc: string;
  zipfiles: IHicustomZipfile[]; // detail_3d.zipfiles
  uv_list: IHicustomUV[]; // detail_3d.uv_list
  primary_uv_name: string; // 这是 material.name（不是 mesh.name），参考 web-zhiwen setMaterialMap
  render_params: IHicustomRenderParams;
  width: number; // 输出宽（一般是 d3d.width）
  height: number;
  /**
   * 相机位置（detail_3d.position，参考 web-zhiwen m-transform.update3d）。
   * 注意不是 render_params.position —— hicustom 返回里这两个是不同对象。
   */
  cameraPosition?: IPosition3;
  /**
   * 其他 uv 的贴图（view_id → 图片 url/base64）。用于截图时同时呈现其它 face 的设计。
   */
  otherUVTextures?: Record<number, string>;
  /**
   * `_base` 材质（产品底色）的贴图 url，取自 models.json colors_data.design_color_url
   * 或 setting.json uv_list[].design_color_custom_base.design_color_url。
   */
  designColorSrc?: string;
  /**
   * `_base` 材质的 RGB（["R","G","B"]，0-255）。hicustom 里是 design_color_code。
   * 贴图本身带布料细节不是纯色，靠 material.color × map 的数学关系强制基色（黑=(0,0,0)→最终黑）。
   */
  designColorCode?: string[];
  /**
   * hicustom colors_data.texture_map：金属/纹理产品的 _base 贴图 PNG。
   * applyTextureMap 把它赋给所有 _base 材质的 mat.map（HN5V75 金色装饰冠的金色花纹）
   */
  textureMap?: string;
  enable_hdr?: number;
}

// 相机 zoom：hicustom chunk-vendors.js 里 `updateZoom(t){ this.camera.zoom=t }`
// 直接用 render_params.zoom 不做任何换算。之前参考 web-zhiwen 里写成 `/0.532`
// （≈ 乘 1.88），导致 renderHicustomOffscreen 镜头比 hicustom 近约 1.88 倍，
// 65ZQ77 Preview 1 看到信箱套贴满画布。已改回直接用

const toNumber = (v: number | string | undefined, fallback = 0) => {
  if (v == null) return fallback;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * 给 scene 加一套统一的灯光：环境光 + 上下前后 4 个方向光。
 * 参考 web-zhiwen/src/use/threeuse/core.js addLights。
 * 尝试过按 hicustom render_params 逐模型布光（mainPointIntensity/type/position 等），
 * 但 hicustom 数据里 intensity 跨模型差异巨大（有的 0.3、有的 3.0），直接用会有的太亮
 * 有的太暗；固定套路反而对各种模型都稳定。
 */
const setupFixedLights = (scene: Scene) => {
  scene.add(new AmbientLight(0xffffff, 0.65));
  const mainIntensity = 0.75;
  const LIGHT_DIST = 10;
  const dirs: Array<[number, number, number]> = [
    [0, 0, LIGHT_DIST],
    [0, 0, -LIGHT_DIST],
    [0, LIGHT_DIST, 0],
    [0, -LIGHT_DIST, 0],
  ];
  for (const [x, y, z] of dirs) {
    const d = new DirectionalLight(0xffffff, mainIntensity);
    d.position.set(x, y, z);
    scene.add(d);
  }
};

/**
 * 按 hicustom render_params 布光。仅 newLightModel === true 时生效，否则回退
 * 固定套路（老数据/无标记的产品继续稳定）。逐产品匹配 hicustom 官方的光照强度，
 * 避免统一套路对不同材质产品要么太亮要么太暗。
 *
 * 解析字段：
 * - ambientLightIntensity → AmbientLight
 * - mainPointType + mainPointPosition + mainPointIntensity → 主光（direct=DirectionalLight / point=PointLight）
 * - aroundPointIntensity > 0 → 模型四周 4 个 PointLight 做环绕补光
 * - lights[] → 具体位置的额外 PointLight（支持 decay/distance/color/closed）
 */
const parseColor = (c?: string, fallback = 0xffffff): number => {
  if (!c) return fallback;
  const hex = c.startsWith('#') ? c.slice(1) : c;
  if (hex.length === 3) {
    return parseInt(
      hex
        .split('')
        .map((x) => x + x)
        .join(''),
      16,
    );
  }
  const n = parseInt(hex, 16);
  return Number.isFinite(n) ? n : fallback;
};

const setupLightsFromRenderParams = (
  scene: Scene,
  params?: IHicustomRenderParams,
) => {
  if (!params) {
    // 完全没 render_params 才兜底 setupFixedLights（老产品 / 非 hicustom 调用）
    setupFixedLights(scene);
    return;
  }
  // hicustom chunk-vendors: `this.renderer.physicallyCorrectLights = newLightModel`
  // → newLightModel=true 走 physical shader（ambient/direct 不 ×π，BRDF /π 一次）；
  //   newLightModel=false/undefined 走 legacy shader（ambient/direct 各 ×π，
  //   再经 BRDF /π 抵消 → effective = 输入值）。
  // Three.js r155+ 固定走 physical shader（没 useLegacyLights 开关），legacy 模式
  // 产品的 light intensity 需要预乘 π 补偿——否则比 hicustom 暗 π ≈ 3.14×
  // （8BRYEC pos=9/11、PYMAQQ 大多数视角、65ZQ77 pos=15 都是 legacy mode）
  const legacyMode = !(params as any).newLightModel;
  const intensityScale = legacyMode ? Math.PI : 1;

  const ambient = toNumber(params.ambientLightIntensity, 0.3);
  scene.add(new AmbientLight(0xffffff, ambient * intensityScale));

  // 主光：DirectionalLight (newLightModel) 或 PointLight。位置由 onBeforeRender
  // 每帧同步成 camera.position，复刻 hicustom "LIGHT_FOLLOW_CAM_EVENT" 机制——
  // 任何相机角度下主光都从相机方向打来，模型背面也有正面照度。
  // lightLock 为真时不跟随，用 mainPointPosition 静态位置（hicustom 同样行为）。
  if (params.mainPointPosition) {
    const intensity =
      toNumber(params.mainPointIntensity, 1) * intensityScale;
    const pos = params.mainPointPosition;
    const isPoint = params.mainPointType === 'point';
    const main = isPoint
      ? new PointLight(0xffffff, intensity, 0, 0)
      : new DirectionalLight(0xffffff, intensity);
    main.position.set(toNumber(pos.x), toNumber(pos.y), toNumber(pos.z));
    scene.add(main);
    const lightLock = (params as any).lightLock === true;
    if (!lightLock) {
      // 用 scene.onBeforeRender 钩子，渲染前把主光位置同步成相机位置
      scene.onBeforeRender = (_renderer, _scene, cam) => {
        main.position.copy(cam.position);
      };
    }
  }

  const around = toNumber(params.aroundPointIntensity, 0);
  if (around > 0) {
    // hicustom chunk-vendors.js 里的 6 方向 hexapod：±X / ±Y / ±Z 各放一颗
    // PointLight 距离 200。缺 ±Y 那两颗（之前我们只放 4 颗在 XZ 平面）会导致
    // 平面 normal=+Y 的全幅产品（H2PXXM 鼠标垫等）在 ambient=0/mainPoint=0
    // 只开 around 的 preview（H2PXXM preview3 这种）下，所有灯都跟表面正交、
    // lambert 全 0 → 整面渲成黑
    const AROUND_DIST = 200;
    const dirs: Array<[number, number, number]> = [
      [0, AROUND_DIST, 0],
      [0, -AROUND_DIST, 0],
      [AROUND_DIST, 0, 0],
      [-AROUND_DIST, 0, 0],
      [0, 0, AROUND_DIST],
      [0, 0, -AROUND_DIST],
    ];
    for (const [x, y, z] of dirs) {
      const p = new PointLight(0xffffff, around * intensityScale, 0, 0);
      p.position.set(x, y, z);
      scene.add(p);
    }
  }

  for (const l of params.lights || []) {
    if (l.closed) continue;
    const rawIntensity = toNumber(l.intensity, 1);
    const rawDistance = toNumber(l.distance, 0);
    const rawDecay = toNumber(l.decay, 0);
    const px = toNumber(l.position.x);
    const py = toNumber(l.position.y);
    const pz = toNumber(l.position.z);
    let intensity: number;
    let distance: number;
    let decay: number;
    if (legacyMode) {
      // hicustom legacy 模式 PointLight 用自定义衰减:
      //   attenuation = pow(saturate(1 - lightDist/distance), decay), or 1 if distance=0
      // 跟 Three.js r173 physical 模式的 `1/d²` 完全不同——不能把 decay/distance
      // 直接传给 Three.js，否则 decay=2 会按物理逆平方算（CP9EK4 pos=9 的 0.5 强度
      // 侧光在物理模式下衰减到 0.002 几乎看不见）。
      // 手算 legacy 衰减（以光到模型原点的距离近似），预乘到 intensity，再
      // 把 Three.js PointLight 设 distance=0/decay=0 让它不做二次衰减
      const lightDist = Math.sqrt(px * px + py * py + pz * pz);
      let legacyAtten = 1;
      if (rawDistance > 0) {
        legacyAtten = Math.pow(
          Math.max(0, 1 - lightDist / rawDistance),
          rawDecay,
        );
      }
      intensity = rawIntensity * legacyAtten * intensityScale;
      distance = 0;
      decay = 0;
    } else {
      intensity = rawIntensity;
      distance = rawDistance;
      decay = rawDecay;
    }
    const p = new PointLight(parseColor(l.color), intensity, distance, decay);
    p.position.set(px, py, pz);
    scene.add(p);
  }
};

const pickMainGlbUrl = (zipfiles: IHicustomZipfile[]) => {
  const glbs = zipfiles.filter((f) => f.ext === 'glb');
  // 习惯上 *_source 是源模型，主渲染模型是不带 _source/_data 的那份
  const main = glbs.find(
    (f) => !f.file_name.endsWith('_source') && !f.file_name.endsWith('_data'),
  );
  return main?.file_url || glbs[0]?.file_url;
};

// 检查模型里是否有同名材质已被 applyModelMaterials 赋过 map（比如 zipfiles 里自带硅胶贴图）
const materialHasMap = (root: Object3D, materialName: string) => {
  let has = false;
  root.traverse((obj) => {
    if (has) return;
    const m = obj as Mesh;
    if (!m.isMesh || !m.material) return;
    const mats = Array.isArray(m.material)
      ? (m.material as MeshStandardMaterial[])
      : [m.material as MeshStandardMaterial];
    for (const mat of mats) {
      if (mat.name === materialName && mat.map) {
        has = true;
        return;
      }
    }
  });
  return has;
};

/**
 * P7NM8U 折叠卡这类 GLB：`_01` 卡身跟 `_A/_B_custom` 共享 vertex buffer，bbox 完
 * 全重叠在同 z 平面 → 像素级 z-fight，旋转时 depth 漂移产生撕纸 dither。对齐
 * hicustom 官网 vendor JS（processMaterial 用 polygonOffset）。
 *
 * 不能动 `_custom`（前置 `_custom` 会让 7QSSMR 披风薄纱、78JOQH 变色杯涂层这种
 * 本该叠在 `_custom` 上方的 BLEND 层被设计穿过）。改成把"跟 `_custom` 共面的
 * OPAQUE 非 `_custom`/`_base` mesh"往后推：
 *   - P7NM8U `_01`（OPAQUE 卡身共面）→ 后推，治 z-fight
 *   - 7QSSMR 披风 / 78JOQH 杯涂层（BLEND, transparent=true）→ 不命中，不动
 *   - BDVMIC 灯座 / JS3TPD 别针（OPAQUE 但不共面）→ overlapRatio 低，不动
 *
 * 必须在 `applyModelMaterials` 之后调用（要 transparent/alphaMode 已设好）。
 */
const applyCoplanarOpaquePolygonOffset = (root: Object3D) => {
  const customBox = new Box3();
  let foundCustom = false;
  root.traverse((obj) => {
    const m = obj as Mesh;
    if (!m.isMesh || !m.material) return;
    const mat = Array.isArray(m.material)
      ? (m.material[0] as MeshStandardMaterial)
      : (m.material as MeshStandardMaterial);
    if (mat?.name?.endsWith('_custom')) {
      customBox.union(new Box3().setFromObject(m));
      foundCustom = true;
    }
  });
  if (!foundCustom) return;
  const cDx = customBox.max.x - customBox.min.x;
  const cDy = customBox.max.y - customBox.min.y;
  const cDz = customBox.max.z - customBox.min.z;
  const customVol = cDx * cDy * cDz;
  if (customVol <= 0) return;
  root.traverse((obj) => {
    const m = obj as Mesh;
    if (!m.isMesh || !m.material) return;
    const mats = Array.isArray(m.material)
      ? (m.material as MeshStandardMaterial[])
      : [m.material as MeshStandardMaterial];
    for (const mat of mats) {
      if (!mat?.name) continue;
      if (mat.name.endsWith('_custom') || mat.name.endsWith('_base')) continue;
      // 只挑 OPAQUE：transparent=true 的 BLEND 层属于"叠在 _custom 上"的装饰
      // （披风/涂层/玻璃罩），不能后推
      if (mat.transparent) continue;
      const mBox = new Box3().setFromObject(m);
      const ox = Math.max(0, Math.min(mBox.max.x, customBox.max.x) - Math.max(mBox.min.x, customBox.min.x));
      const oy = Math.max(0, Math.min(mBox.max.y, customBox.max.y) - Math.max(mBox.min.y, customBox.min.y));
      const oz = Math.max(0, Math.min(mBox.max.z, customBox.max.z) - Math.max(mBox.min.z, customBox.min.z));
      const overlapVol = ox * oy * oz;
      // 共面判定：覆盖大半个 _custom 区域才推后。BDVMIC 灯座、JS3TPD 别针
      // 跟 _custom 不共面，overlap 比例低 → 不命中
      if (overlapVol / customVol > 0.5) {
        mat.polygonOffset = true;
        mat.polygonOffsetFactor = 2;
        mat.polygonOffsetUnits = 4;
        mat.needsUpdate = true;
      }
    }
  });
};

// hicustom uv.name 对应的是 material.name（不是 mesh.name）。遍历所有 mesh 的材质按名匹配
const applyTextureToMaterial = (
  root: Object3D,
  materialName: string,
  tex: Texture,
) => {
  let applied = false;
  root.traverse((obj) => {
    const m = obj as Mesh;
    if (!m.isMesh) return;
    const handle = (mm: MeshStandardMaterial) => {
      if (mm.name !== materialName) return;
      if (mm.map && mm.map !== tex) mm.map.dispose();
      mm.map = tex;
      // alphaTest + alphaToCoverage：边缘靠 MSAA 采样软切，比单纯 alphaTest=0.5
      // 硬切平滑很多（text/logo 抗锯齿边缘不再出阶梯）。alphaToCoverage 要
      // renderer antialias=true 才生效（我们 getSharedRenderer 里是开的）。
      // 用 0.1 低阈值只丢真 0 alpha 的像素，边缘半透明像素都留，靠 MSAA 覆盖率
      // 决定像素最终不透明度。不走 alpha blending → 不复现 DoubleSide 的 z-sort 问题
      mm.alphaTest = 0.1;
      mm.alphaToCoverage = true;
      mm.transparent = false;
      // side 已由 applyModelMaterials 按 cfg.doubleSided 设好（只有显式
      // `false` 才 FrontSide，其余 DoubleSide 兜底），这里不再覆盖
      mm.needsUpdate = true;
      applied = true;
    };
    const mat = m.material as MeshStandardMaterial | MeshStandardMaterial[];
    if (Array.isArray(mat)) mat.forEach(handle);
    else if (mat) handle(mat);
  });
  return applied;
};

/**
 * view_id → 2D 设计画布的 base64。当任何一个 face 的子场景保存时更新；
 * 离屏 3D 渲染读取各 view_id 的当前设计作为对应 mesh 的贴图。
 */
export const hicustomFaceDesigns = new Map<number, string>();

export const setHicustomFaceDesign = (viewId: number, base64: string) => {
  hicustomFaceDesigns.set(viewId, base64);
};

export const getHicustomFaceDesign = (viewId: number) =>
  hicustomFaceDesigns.get(viewId);

// 模块级 Map 跨路由不会清，进入新的 Design2d 前必须重置，
// 否则新产品的 3D 预览会贴上一个产品残留的 view_id 贴图
export const clearHicustomFaceDesigns = () => {
  hicustomFaceDesigns.clear();
};

// hicustom 官网 app.js 里 Design All（"整体设计"，id=9999）的 print_area 是写死常量
// view_width=700/width=700/height=700，跟具体产品的 print_area / 物理尺寸无关 ——
// 所有 hicustom 产品的 Design All 画布 mm 都按 700×700 显示。我们对齐这个语义。
//   `Ye = { id: 9999, name: "整体设计",
//           print_area: { view_width: 700, width: 700, height: 700, ... } }`
// （Playwright 在 P9I3CP 验证：state.view_id=9999 时面板显示 "可设计区域 70.0x70.0 (cm)"）
export const HICUSTOM_DESIGN_ALL_RECT_MM = { width: 700, height: 700 };

// ---- point_svg 即时栅格化 ----
// 把 scene.components 里 name === 'point_svg' 的 sprite 的 SVG URL texture
// 用 <img> + canvas 转成 PNG data URL，原地替换。只处理 URL（非 data:），
// 同一个 URL 会命中 pointSvgPngCache 复用结果，避免重复 canvas 解码。
// 用途：ComponentScene 加载 subScene 前调一次。保存的 JSON 里只存 URL，
// 运行时经过这一步之后 Pixi 拿到的是它能解码的 PNG data URL。
const pointSvgPngCache = new Map<string, Promise<string>>();

const rasterizeSvgUrlToPng = (url: string): Promise<string> => {
  const cached = pointSvgPngCache.get(url);
  if (cached) return cached;
  const pending = new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const w = img.naturalWidth || 917;
      const h = img.naturalHeight || 1259;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w));
      canvas.height = Math.max(1, Math.round(h));
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('[hicustom] no 2d ctx'));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () =>
      reject(new Error('[hicustom] rasterize point_svg failed'));
    img.src = url;
  });
  pointSvgPngCache.set(url, pending);
  return pending;
};

export const rasterizeHicustomPointSvg = async (scene: IScene2d) => {
  const tasks: Promise<void>[] = [];
  for (const comp of scene.components) {
    if (comp.type !== 'sprite') continue;
    const p = comp.props as { name?: string; texture?: string };
    if (p.name !== 'point_svg') continue;
    const src = p.texture;
    if (!src || src.startsWith('data:')) continue;
    tasks.push(
      rasterizeSvgUrlToPng(src)
        .then((dataUrl) => {
          p.texture = dataUrl;
        })
        .catch((e) => {
          console.warn('[hicustom] point_svg rasterize skip', src, e);
        }),
    );
  }
  await Promise.all(tasks);
};

// ---- hicustom part screenshot 专用合成 ----
// 用途：SceneForm.clickSave 里为非当前 part 生成封面图，避免走 core.reloadParts 的
// 全量 pixi render2d。hicustom 每个 sprite 的 props.texture 已经是 3D 预渲染好的
// 合成 base64（renderHicustomOffscreen 产物 + ComponentScene.clickDone 跨 part 同步），
// 直接在 2D canvas 上按 z 序、position/anchor/scale/angle/alpha 叠加就能得到和 pixi
// 一样的视觉，省掉 pixi app mount / destroy + 纹理重上传的开销。

const imgCache = new Map<string, Promise<HTMLImageElement>>();

const loadImageFromSrc = (src: string): Promise<HTMLImageElement> => {
  const cached = imgCache.get(src);
  if (cached) return cached;
  const pending = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`[hicustom] image load failed`));
    img.src = src;
  });
  imgCache.set(src, pending);
  return pending;
};

// 仅 sprite 类型可走轻量 2D canvas drawImage 路径；其它类型（如 perspective_mesh、
// tiling_sprite）必须走 Pixi 临时 mount/extract，否则被静默 skip 会导致截图缺该层。
// 例：hicustom lifestyle 预览（1292_fbxw 的 preview 10/11/12）用 perspective_mesh
// 把用户设计扭到 lifestyle 照片对应位置，2D canvas 路径会丢掉这层 → 看到底图毛毯
// 没有设计覆盖，呈现"设计是黑的"。
const sceneNeedsPixiCompose = (scene: IScene2d) =>
  scene.components.some(
    (c) => c.type !== 'sprite' && c.props.visible !== false,
  );

const buildPixiCompForCompose = async (
  comp: IScene2d['components'][number],
): Promise<PixiContainer | null> => {
  const p = comp.props as any;
  if (comp.type === 'sprite') {
    const tex = await loadTexture(p.texture || '');
    const s = new PixiSprite(tex);
    if (p.tint) s.tint = String(p.tint).replace('#', '0x');
    s.position.set(p.position?.x ?? 0, p.position?.y ?? 0);
    s.anchor.set(p.anchor?.x ?? 0, p.anchor?.y ?? 0);
    s.scale.set(p.scale?.x ?? 1, p.scale?.y ?? 1);
    if (p.width && p.height) {
      s.width = p.width;
      s.height = p.height;
    } else if (p.width) {
      s.width = p.width;
      s.height = (tex.height / tex.width) * p.width;
    } else if (p.height) {
      s.height = p.height;
      s.width = (tex.width / tex.height) * p.height;
    }
    if (p.blendMode != null) s.blendMode = p.blendMode as BLEND_MODES;
    s.angle = p.angle ?? 0;
    s.alpha = p.alpha ?? 1;
    return s;
  }
  if (comp.type === 'perspective_mesh') {
    const tex = await loadTexture(p.texture || '');
    const m = new PixiPerspectiveMesh({
      texture: tex,
      verticesX: 20,
      verticesY: 20,
      x0: p.corner0?.x ?? 0,
      y0: p.corner0?.y ?? 0,
      x1: p.corner1?.x ?? 0,
      y1: p.corner1?.y ?? 0,
      x2: p.corner2?.x ?? 0,
      y2: p.corner2?.y ?? 0,
      x3: p.corner3?.x ?? 0,
      y3: p.corner3?.y ?? 0,
    });
    m.alpha = p.alpha ?? 1;
    return m;
  }
  return null;
};

// scene 内含非 sprite 组件时走这条：临时 mount 一个 offscreen Pixi.Application，
// 用 extract.base64 拿和实时预览一致的渲染结果，省掉 reloadParts 那种"换 currentPart
// → 实时画布闪烁"的代价，但比纯 2D canvas 慢；只在必要时使用。
const composeHicustomPartScreenshotViaPixi = async (
  scene: IScene2d,
): Promise<string> => {
  const w = scene.app.width;
  const h = scene.app.height;
  const app = new PixiApplication();
  await app.init({
    width: w,
    height: h,
    backgroundAlpha: 0,
    antialias: true,
    autoStart: false,
    resolution: 1,
  });
  try {
    if (scene.app.backgroundColor) {
      app.renderer.background.color = scene.app.backgroundColor.replace(
        '#',
        '0x',
      );
    }
    const sorted = [...scene.components]
      .filter((c) => c.props.visible !== false)
      .sort((a, b) => (a.props.zIndex ?? 0) - (b.props.zIndex ?? 0));
    for (const comp of sorted) {
      try {
        const obj = await buildPixiCompForCompose(comp);
        if (obj) app.stage.addChild(obj);
      } catch (e) {
        console.warn(
          '[hicustom screenshot pixi] skip comp',
          (comp.props as any)?.name,
          e,
        );
      }
    }
    const dataUrl = await app.renderer.extract.base64({
      target: app.stage,
      frame: new PixiRectangle(0, 0, w, h),
    });
    return dataUrl || '';
  } finally {
    app.destroy(true, { children: true, texture: false, textureSource: false });
  }
};

export const composeHicustomPartScreenshot = async (
  scene: IScene2d,
): Promise<string> => {
  if (sceneNeedsPixiCompose(scene)) {
    return composeHicustomPartScreenshotViaPixi(scene);
  }

  const w = scene.app.width;
  const h = scene.app.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  if (scene.app.backgroundColor) {
    ctx.fillStyle = scene.app.backgroundColor;
    ctx.fillRect(0, 0, w, h);
  }

  // 跟 pixi 一致按 zIndex 升序绘制，不可见的跳过
  const comps = [...scene.components]
    .filter((c) => c.type === 'sprite' && c.props.visible !== false)
    .sort((a, b) => (a.props.zIndex ?? 0) - (b.props.zIndex ?? 0));

  for (const comp of comps) {
    const p = comp.props as any;
    const src = p.texture;
    if (!src) continue;
    try {
      const img = await loadImageFromSrc(src);
      const spriteW = p.width ?? img.naturalWidth;
      const spriteH = p.height ?? img.naturalHeight;
      const anchorX = p.anchor?.x ?? 0;
      const anchorY = p.anchor?.y ?? 0;
      const scaleX = p.scale?.x ?? 1;
      const scaleY = p.scale?.y ?? 1;
      const angle = p.angle ?? 0;
      const alpha = p.alpha ?? 1;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(p.position?.x ?? 0, p.position?.y ?? 0);
      if (angle) ctx.rotate((angle * Math.PI) / 180);
      if (scaleX !== 1 || scaleY !== 1) ctx.scale(scaleX, scaleY);
      ctx.drawImage(
        img,
        -spriteW * anchorX,
        -spriteH * anchorY,
        spriteW,
        spriteH,
      );
      ctx.restore();
    } catch (e) {
      console.warn('[hicustom screenshot] skip comp', p.name, e);
    }
  }

  return canvas.toDataURL('image/png');
};

export const reloadHicustomScreenshots = async (
  product: IProduct,
  opts?: { skip?: string[] },
) => {
  for (const part of product.parts) {
    if (opts?.skip?.includes(part.name)) continue;
    const template = part.templates.find(
      (t) => t.id === part.currentTemplateId,
    );
    if (!template) continue;
    const scene = product.scenes[template.scene_id];
    if (!scene) continue;
    try {
      const dataUrl = await composeHicustomPartScreenshot(scene);
      if (dataUrl) product.screenshots[part.name] = dataUrl;
    } catch (e) {
      console.error('[hicustom] reloadScreenshots failed', part.name, e);
    }
  }
};

const glbCache = new Map<string, Promise<Object3D>>();
// 深克隆：几何可复用，但材质/贴图每次独立，避免并发渲染互相覆写 map
const cloneMeshMaterials = (root: Object3D) => {
  root.traverse((obj) => {
    const m = obj as Mesh;
    if (!m.isMesh || !m.material) return;
    if (Array.isArray(m.material)) {
      m.material = m.material.map((mm) => (mm as Material).clone());
    } else {
      m.material = (m.material as Material).clone();
    }
  });
};
const getGlb = async (url: string): Promise<Object3D> => {
  let pending = glbCache.get(url);
  if (!pending) {
    pending = loadGLTF(url);
    glbCache.set(url, pending);
  }
  const orig = await pending;
  const cloned = orig.clone(true);
  cloneMeshMaterials(cloned);
  return cloned;
};

const materialsJsonCache = new Map<string, Promise<any>>();
const loadMaterialsJson = (url: string) => {
  let pending = materialsJsonCache.get(url);
  if (!pending) {
    pending = axios.get(url).then((r) => r.data);
    materialsJsonCache.set(url, pending);
  }
  return pending;
};

// 从材质名里解析 "_scaleN&N" 得到重复次数（非 custom 材质用来平铺底色/法线）
const parseScaleFromName = (name: string) => {
  if (!name.includes('_scale')) return 1;
  const n = Number(name.split('_scale')[1].split('&')[0]);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const applyRepeat = (tex: Texture, sx: number, sy?: number) => {
  const repeatY = sy ?? sx;
  if (sx === 1 && repeatY === 1) return;
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  // web-zhiwen 使用负值 repeat，hicustom 模型 UV 方向约定
  tex.repeat.set(-sx, -repeatY);
};

// 优先读 GLTF 标准扩展 KHR_texture_transform.scale（WWSUYZ 背包 `_06` 网眼袋
// scale=[40,30]，无 `_scaleN&N` 命名后缀），sx/sy 都正才用；fallback 到 hicustom
// `_scaleN&N` 命名约定（对称缩放）。BDVMIC `_08_D` scale=[0,0] 是数据 bug，
// 走 fallback 避免贴图被 squash 到 0
const resolveTextureScale = (
  matName: string,
  textureExtensions: any,
): { sx: number; sy: number } => {
  const khr = textureExtensions?.KHR_texture_transform;
  const arr = khr?.scale;
  if (Array.isArray(arr) && arr.length >= 2) {
    const sx = Number(arr[0]);
    const sy = Number(arr[1]);
    if (Number.isFinite(sx) && Number.isFinite(sy) && sx > 0 && sy > 0) {
      return { sx, sy };
    }
  }
  const s = parseScaleFromName(matName);
  return { sx: s, sy: s };
};

/**
 * 把 zipfiles 里的 JSON（GLTF materials config）+ png 纹理应用到模型。
 * 参考 web-zhiwen useThree.js setMaterialsMap。没加载这步的话，除了用户设计区域，
 * 其它材质都是默认的灰/白/透明，整个模型看着就跟 hicustom 预览差别很大。
 */
const applyModelMaterials = async (
  root: Object3D,
  zipfiles: IHicustomZipfile[],
  disposables: Texture[],
) => {
  const jsonFile = zipfiles.find((f) => f.ext === 'json');
  if (!jsonFile) return;
  let materialsJson: any;
  try {
    materialsJson = await loadMaterialsJson(jsonFile.file_url);
  } catch (e) {
    console.warn('[hicustom] load materials json failed', e);
    return;
  }
  const textures: Array<{ name?: string }> = materialsJson?.textures || [];
  const materials: Array<any> = materialsJson?.materials || [];

  const lookupMap = new Map<string, any>();
  for (const mat of materials) {
    if (mat?.name) lookupMap.set(mat.name, mat);
  }

  const tasks: Promise<void>[] = [];
  root.traverse((obj) => {
    const mesh = obj as Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const mats = Array.isArray(mesh.material)
      ? (mesh.material as MeshStandardMaterial[])
      : [mesh.material as MeshStandardMaterial];
    for (const mat of mats) {
      const cfg = lookupMap.get(mat.name);
      if (!cfg) continue;

      // side 按 sidecar JSON 的 doubleSided 决定（跟 hicustom GLTFLoader 一致：
      // `!0===a.doubleSided&&(s.side=2)` —— 只有显式 `true` 才双面，其余
      // FrontSide）。JS3TPD 球壳 undefined → FrontSide，别针应该被 _custom
      // 前板挡住（靠下面 multi-mesh 不 discard 路径保证 _custom 是 opaque）。
      // 44EBO6 袜子 undefined → FrontSide，内壁 backface 被 cull 正确
      mat.side = cfg.doubleSided ? DoubleSide : FrontSide;

      // opacity (baseColorFactor.a)。transparent 必须按 alphaMode 决定，不能
      // 只看 bcf[3]<1：WWSUYZ 背包 `Luggage_6690N_06` 网眼袋是 alphaMode=BLEND
      // + bcf=[0,0,0,1]，alpha 在 baseColorTexture 的 alpha 通道（PNG 镂空）。
      // 简单按 bcf[3]<1 设 transparent=false 会把 GLTFLoader 已经按 alphaMode
      // 设好的 transparent=true 覆写掉，blending 关闭后贴图 alpha 失效，渲成
      // 实心 bcf 颜色（黑块）
      const baseColorFactor = cfg.pbrMetallicRoughness?.baseColorFactor;
      if (Array.isArray(baseColorFactor) && baseColorFactor.length >= 4) {
        mat.opacity = baseColorFactor[3];
      }
      mat.transparent =
        cfg.alphaMode === 'BLEND' ||
        (Array.isArray(baseColorFactor) &&
          baseColorFactor.length >= 4 &&
          baseColorFactor[3] < 1);

      // baseColor map —— GLB 内嵌 UV 约定 flipY=false（GLTFLoader 加载时也是这值）
      const baseColorTexture = cfg.pbrMetallicRoughness?.baseColorTexture;
      const baseIdx = baseColorTexture?.index;
      const baseScale = resolveTextureScale(mat.name, baseColorTexture?.extensions);
      if (baseIdx != null) {
        const texCfg = textures[baseIdx];
        const file = zipfiles.find((f) => f.file_name === texCfg?.name);
        if (file) {
          tasks.push(
            loadTextureFromSrc(file.file_url, { flipY: false }).then((tex) => {
              applyRepeat(tex, baseScale.sx, baseScale.sy);
              if (mat.map) mat.map.dispose();
              mat.map = tex;
              mat.needsUpdate = true;
              disposables.push(tex);
            }),
          );
        }
      }

      // normal map（线性空间 + flipY=false）
      // hicustom GLB 来自 Plattar UnityGLTF，Unity 法线贴图在 three.js 里需要
      // Y 分量取反才能正确受光（参考 hicustom processMaterial: `normalScale.set(l, -l)`）。
      // 不取反会让 BLEND 半透 mesh 的高光算错，看起来像雾化覆盖
      if (cfg.normalTexture?.index != null) {
        const nCfg = textures[cfg.normalTexture.index];
        const nFile = zipfiles.find((f) => f.file_name === nCfg?.name);
        const normalScale = resolveTextureScale(
          mat.name,
          cfg.normalTexture?.extensions,
        );
        if (nFile) {
          tasks.push(
            loadDataTextureFromSrc(nFile.file_url).then((tex) => {
              tex.flipY = false;
              applyRepeat(tex, normalScale.sx, normalScale.sy);
              if (mat.normalMap) mat.normalMap.dispose();
              mat.normalMap = tex;
              const s = cfg.normalTexture.scale ?? 1;
              mat.normalScale = new Vector2(s, -s);
              mat.needsUpdate = true;
              disposables.push(tex);
            }),
          );
        }
      }

      // metalness / roughness factor —— 显式 apply JSON sidecar 的标量值，
      // 跟 hicustom processMaterial: `r.metalness = c; r.roughness = u` 对齐。
      // GLTFLoader 加载 GLB 已经设过一遍（GLB 跟 sidecar 一致时无副作用），
      // 但 BDVMIC 这种 GLB textures count=0 + sidecar 携带全部 PBR 数据的产品，
      // 必须以 sidecar 为准
      const pbr = cfg.pbrMetallicRoughness || {};
      if (typeof pbr.metallicFactor === 'number') {
        mat.metalness = pbr.metallicFactor;
      }
      if (typeof pbr.roughnessFactor === 'number') {
        mat.roughness = pbr.roughnessFactor;
      }

      // metallicRoughnessTexture —— hicustom 把这同一张贴图分别赋给
      // metalnessMap 和 roughnessMap（three.js 约定：B 通道=metalness，
      // G 通道=roughness）。flipY=false 保 GLTF 约定
      if (pbr.metallicRoughnessTexture?.index != null) {
        const mrCfg = textures[pbr.metallicRoughnessTexture.index];
        const mrFile = zipfiles.find((f) => f.file_name === mrCfg?.name);
        if (mrFile) {
          tasks.push(
            loadDataTextureFromSrc(mrFile.file_url).then((tex) => {
              tex.flipY = false;
              if (mat.metalnessMap) mat.metalnessMap.dispose();
              if (mat.roughnessMap) mat.roughnessMap.dispose();
              mat.metalnessMap = tex;
              mat.roughnessMap = tex;
              mat.needsUpdate = true;
              disposables.push(tex);
            }),
          );
        }
      }
    }
  });
  await Promise.all(tasks);
};

/**
 * PNG 头 colorType byte（offset 25）：
 *   0=greyscale / 2=RGB → 必无 alpha 通道
 *   3=palette → **可能**有 alpha（通过 tRNS chunk 给每个调色板 entry 配 alpha
 *               值，H2PXXM 鼠标垫 texture_map 就是这种 P 模式 + tRNS=[0,...]
 *               让索引 0 全透）
 *   4=greyscale+alpha / 6=RGBA → 通常带 alpha
 * 0/2 直接判 opaque，3/4/6 都得扫像素（palette 不扫的话调色板 alpha 全漏判，
 * 错把 BLEND 翻成 opaque 盖死下层 _custom）
 */
const pngHasAlphaChannel = (bytes: Uint8Array): boolean => {
  if (bytes.length < 26) return false;
  if (bytes[0] !== 0x89 || bytes[1] !== 0x50) return false;
  const colorType = bytes[25];
  return colorType === 3 || colorType === 4 || colorType === 6;
};

/** RGBA PNG 还要看 alpha 直方图：≥5% 像素 alpha<128 才算"实质透明" */
const bytesHaveMeaningfulAlpha = async (
  bytes: Uint8Array,
): Promise<boolean> => {
  if (!pngHasAlphaChannel(bytes)) return false;
  try {
    const blob = new Blob([bytes], { type: 'image/png' });
    const bmp = await createImageBitmap(blob);
    const c = document.createElement('canvas');
    c.width = 64;
    c.height = 64;
    const ctx = c.getContext('2d');
    if (!ctx) {
      bmp.close();
      return false;
    }
    ctx.drawImage(bmp, 0, 0, 64, 64);
    bmp.close();
    const data = ctx.getImageData(0, 0, 64, 64).data;
    let lowCount = 0;
    const total = data.length / 4;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 128) lowCount += 1;
    }
    return lowCount / total >= 0.05;
  } catch {
    return false;
  }
};

/**
 * 给名字以 `_base` 结尾的所有材质设 `mat.map = textureMap` —— 对齐 hicustom
 * vendor JS：`l.forEach(t=>{ c[t].map = u })` 其中 l=baseMats, u=textureMap。
 * texture_map 是 hicustom 颜色变体里包含的"产品材质纹理 PNG"（HN5V75 金色
 * 装饰冠的金色花纹、78JOQH 变色杯的 alpha 镂空涂层）。空 url 时跳过。
 *
 * 实现：自己 fetch PNG bytes 当唯一来源，先按 PNG 头 colorType 分流：
 *   - 0/2/3（无 alpha 通道，QVYQRA 皮料 RGB）→ hasAlpha=false 直接走
 *   - 4/6（有 alpha 通道）→ createImageBitmap + canvas 扫直方图判
 *     "实质透明"（78JOQH 镂空涂层 37% < 128 → true；HN5V75 金色花纹
 *     RGBA 但 alpha 全 255 → false）
 * 同一份字节再 blob URL 喂 TextureLoader（不二次请求）。
 *
 * 按 hasAlpha 同步设 transparent / depthWrite：
 *   - hasAlpha → 保留 BLEND（transparent=true, depthWrite=false），让 78JOQH
 *     涂层的 alpha 通道露出底层 _01
 *   - !hasAlpha → 强行 opaque（transparent=false, depthWrite=true）。
 *     必须翻 depthWrite——GLTFLoader 给 BLEND 材质默认 depthWrite=false，
 *     仅 transparent=false 不写深度，会被后渲的内壁/反面像素覆盖，肉眼
 *     就是 QVYQRA 包体只剩边缘那种"透"
 */
const applyTextureMap = async (
  root: Object3D,
  url: string | undefined,
  disposables: Texture[],
) => {
  if (!url) return;

  let bytes: Uint8Array | null = null;
  try {
    const resp = await fetch(url);
    if (resp.ok) bytes = new Uint8Array(await resp.arrayBuffer());
  } catch {
    bytes = null;
  }

  const hasAlpha = bytes ? await bytesHaveMeaningfulAlpha(bytes) : false;

  let tex: Texture | null = null;
  if (bytes) {
    const blob = new Blob([bytes], { type: 'image/png' });
    const blobUrl = URL.createObjectURL(blob);
    try {
      tex = await loadTextureFromSrc(blobUrl);
    } catch {
      URL.revokeObjectURL(blobUrl);
      return;
    }
    URL.revokeObjectURL(blobUrl);
  } else {
    try {
      tex = await loadTextureFromSrc(url);
    } catch {
      return;
    }
  }
  disposables.push(tex);

  root.traverse((obj) => {
    const mesh = obj as Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const mats = Array.isArray(mesh.material)
      ? (mesh.material as MeshStandardMaterial[])
      : [mesh.material as MeshStandardMaterial];
    for (const mat of mats) {
      if (!mat.name.endsWith('_base')) continue;
      if (mat.map) mat.map.dispose();
      mat.map = tex;
      mat.transparent = hasAlpha;
      mat.depthWrite = !hasAlpha;
      // 标 hasAlpha=true 的 _base：texture_map 是带 alpha 镂空的层（H2PXXM 鼠标垫
      // _03_base palette + tRNS 镂空、78JOQH 变色杯涂层）。下游 applyDesignColor
      // 不能把它翻回 opaque，否则会盖死下层 _custom 的设计
      (mat.userData as any).hicustomKeepBlend = hasAlpha;
      mat.needsUpdate = true;
    }
  });
};

/**
 * 给名字以 `_base` 结尾的材质刷产品底色。参考 web-zhiwen useThree.js setColor。
 * - `code` 走 material.color：color × map 的数学让纹理不论明暗都被压成目标色（黑=(0,0,0)→最终黑）
 * - `src` 仍作为 map 叠布料细节；不设 transparent=true，避免边缘抬灰
 */
const applyDesignColor = async (
  root: Object3D,
  src: string | undefined,
  code: string[] | undefined,
  disposables: Texture[],
) => {
  // 78JOQH 变色杯 mountHicustom3d 调用时 src='' / code=[]（空数组在 JS 里 truthy
  // 让外层 if(src||code) 误判进来）；下面无差别 mat.transparent=false 会把
  // applyModelMaterials 已按 alphaMode='BLEND' 设好的 _02_base 改成 opaque，
  // 蒙版 PNG 的 alpha 通道失效 → 整片黑+星，3D 看不到中段反白。这里加严返回条件
  const hasSrc = !!src;
  const hasCode = !!(code && code.length >= 3);
  if (!hasSrc && !hasCode) return;
  const tasks: Promise<void>[] = [];
  root.traverse((obj) => {
    const mesh = obj as Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const mats = Array.isArray(mesh.material)
      ? (mesh.material as MeshStandardMaterial[])
      : [mesh.material as MeshStandardMaterial];
    for (const mat of mats) {
      if (!mat.name.endsWith('_base')) continue;
      // applyTextureMap 标过 hicustomKeepBlend=true 的是 alpha 镂空"装饰
      // overlay" 层（H2PXXM 鼠标垫 _03_base 横向 woven fabric 纹理、78JOQH
      // 变色杯涂层），不属于"产品本色"。color/transparent 都不能动：
      //   - mat.color = body code 会把装饰条整片染色（H2PXXM 设了
      //     ['0','0','0'] 后 _03_base fabric 变黑盖死下层 bears 设计）
      //   - mat.transparent=false 把 BLEND 翻 opaque 同样盖死下层
      // 没装饰 overlay 的 _base（65ZQ77 信箱套等）维持原 force opaque + 刷
      // body color
      const isOverlay = !!(mat.userData as any)?.hicustomKeepBlend;
      if (hasCode && !isOverlay) {
        mat.color.setRGB(
          toNumber(code![0]) / 255,
          toNumber(code![1]) / 255,
          toNumber(code![2]) / 255,
        );
      }
      if (!isOverlay) {
        mat.transparent = false;
      }
      if (hasSrc) {
        tasks.push(
          loadTextureFromSrc(src, { flipY: false })
            .then((tex) => {
              if (mat.map) mat.map.dispose();
              mat.map = tex;
              mat.needsUpdate = true;
              disposables.push(tex);
            })
            .catch(() => {
              // hicustom CDN 上 design_color.png 经常 404（如 7SXXN4 男士睡裤），
              // 但他们官网渲染器并不强依赖这张图。fallback：保留 code 已写入的 RGB
              mat.needsUpdate = true;
            }),
        );
      } else {
        mat.needsUpdate = true;
      }
    }
  });
  await Promise.all(tasks);
};

const textureLoader = new TextureLoader();
textureLoader.setCrossOrigin('anonymous');

// color map: 要按 sRGB 解码后再用于光照计算
// flipY 保持默认 true —— hicustom GLB 的 UV 期望这样，显式设 false 会导致贴图上下颠倒
const loadTextureFromSrc = (
  src: string,
  options?: { flipY?: boolean },
): Promise<Texture> =>
  new Promise((resolve, reject) => {
    textureLoader.load(
      src,
      (tex) => {
        tex.colorSpace = SRGBColorSpace;
        if (options?.flipY !== undefined) tex.flipY = options.flipY;
        resolve(tex);
      },
      undefined,
      reject,
    );
  });

// data map（normalMap / metalnessMap 等）: 必须保持线性空间，不能按 sRGB 解码
const loadDataTextureFromSrc = (src: string): Promise<Texture> =>
  new Promise((resolve, reject) => {
    textureLoader.load(src, (tex) => resolve(tex), undefined, reject);
  });

// 全局复用 WebGL renderer，避免每张离屏渲染都重建 WebGL context（~50ms）。
let sharedHostCanvas: HTMLCanvasElement | null = null;
let sharedRenderer: WebGLRenderer | null = null;

// hicustom 预置 Studio 全景环境贴图（1500×750 equirectangular PNG），
// 给金属/玻璃材质提供反射，HN5V75 挂件的玻璃质感和金色花纹反射都靠它
const HICUSTOM_ENV_URL_FAN =
  'https://eu1.oss-accelerate.aliyuncs.com/editor/hicustom-env/Studio_fan.png';
const HICUSTOM_ENV_URL_PLAIN =
  'https://eu1.oss-accelerate.aliyuncs.com/editor/hicustom-env/Studio.png';

// envMap 必须用"最终渲染这个 scene 的 renderer"做 PMREM，否则跨 WebGL context
// texture uniform 失效 → 金属/玻璃只吃直接光的 specular 看起来像一个 sun。
// 按 (renderer, 'fan'|'plain') 缓存（WeakMap 自动回收）。renderHicustomOffscreen
// 用 sharedRenderer；mountHicustom3d 用外部 core.store3d.renderer
const envTextureCacheByRenderer = new WeakMap<
  WebGLRenderer,
  Map<'fan' | 'plain', Promise<Texture>>
>();

const getEnvTexture = (
  renderer: WebGLRenderer,
  mainPointType?: string,
): Promise<Texture> => {
  const key: 'fan' | 'plain' = mainPointType === 'direct' ? 'fan' : 'plain';
  let cache = envTextureCacheByRenderer.get(renderer);
  if (!cache) {
    cache = new Map();
    envTextureCacheByRenderer.set(renderer, cache);
  }
  const cached = cache.get(key);
  if (cached) return cached;
  const url = key === 'fan' ? HICUSTOM_ENV_URL_FAN : HICUSTOM_ENV_URL_PLAIN;
  const p = (async () => {
    // 对齐 hicustom `convertEnvMap`: flipY=false + sRGB + PMREM equirectangular
    const raw = await loadTextureFromSrc(url, { flipY: false });
    raw.mapping = EquirectangularReflectionMapping;
    raw.colorSpace = SRGBColorSpace;
    const pmrem = new PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    const rt = pmrem.fromEquirectangular(raw);
    pmrem.dispose();
    raw.dispose();
    return rt.texture;
  })();
  cache.set(key, p);
  return p;
};

// 对齐 hicustom：把 envMap 设到每个 mesh 的 material 上（不用 scene.environment），
// 这样金属/玻璃 mesh 的反射强度跟 hicustom 一致。普通 opaque 材质同样受益于 IBL
// indirect diffuse，整体不会变得太暗。
// 注：Three.js r173 vs r140 在低 roughness 时的 PBR 反射锐利度有算法差异
// （r155+ textureCubeUV 的 mip LOD 公式改过），HN5V75 这类 metal=1/rough=0
// mirror 玻璃件看起来会比 hicustom 官网柔和一点——已接受此差异，不调 intensity
const applyEnvMapPerMaterial = (root: Object3D, envMap: Texture) => {
  root.traverse((obj) => {
    const m = obj as Mesh;
    if (!m.isMesh || !m.material) return;
    const mats = Array.isArray(m.material)
      ? (m.material as MeshStandardMaterial[])
      : [m.material as MeshStandardMaterial];
    for (const mat of mats) {
      if (!mat) continue;
      mat.envMap = envMap;
      mat.needsUpdate = true;
    }
  });
};

const getSharedRenderer = () => {
  if (sharedRenderer && sharedHostCanvas) {
    return {
      renderer: sharedRenderer,
      hostCanvas: sharedHostCanvas,
    };
  }
  sharedHostCanvas = document.createElement('canvas');
  sharedRenderer = new WebGLRenderer({
    canvas: sharedHostCanvas,
    alpha: true,
    antialias: true,
    preserveDrawingBuffer: true,
  });
  sharedRenderer.setPixelRatio(1);
  sharedRenderer.outputColorSpace = SRGBColorSpace;
  // 默认 clearAlpha=1（黑底不透明）即使 alpha:true 也会把背景涂黑，
  // 导致 toDataURL 产出黑色底。改 0 让 mesh 之外的像素透明，合成到
  // detail.image（hicustom 暖色场景图）上就能拿到跟 hicustom 一致的预览
  sharedRenderer.setClearColor(0x000000, 0);
  // 不走 tonemapping：hicustom chunk-vendors 默认 `toneMapping = 0`
  // (NoToneMapping) + outputEncoding sRGB，没改过。我们跟齐
  return {
    renderer: sharedRenderer,
    hostCanvas: sharedHostCanvas,
  };
};

/**
 * 用离屏 WebGL renderer 把 2D 设计画布贴到指定 3D mesh，再按 Hicustom 的相机/灯光设置截图。
 * 返回 dataURL（base64 png），调用方把它作为 sprite.texture。
 */
// 1×1 透明 PNG —— hi sprite 在 renderAllHicustomInitial 渲出真图之前的占位
// （不能用空串：pixi loadTexture 会 fallback Texture.WHITE 16×16 白）。也给
// `renderAllHicustomInitial` 的幂等判断用（见该函数里的比较）
export const HICUSTOM_INIT_TEXTURE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=';
// perspective_mesh 未设计时的占位：1×1 RGBA(200,220,255,30) 淡蓝 ~12% alpha。
// 全透 PNG 时 OutlineFilter 没 alpha 可描边，hover 无视觉反馈（"铺满 bg 后才有高亮"）。
// 加一点点 alpha 让 filter 有边可描 + 给用户淡蓝视觉提示"这里是设计区域"
export const HICUSTOM_DISTORT_INIT_TEXTURE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4nGM4cee/HAAH1QLC5OoUQAAAAABJRU5ErkJggg==';

// hicustom 3D 离屏渲染的输出分辨率：按 sprite 在 canvas 上的 display 尺寸自适应。
// `hc.width/height` 是 hicustom 600 坐标空间的 d3d 尺寸，乘 1024/600 近似得 display
// 像素。小 sprite 最少 1024 保证 2D 画布不会糊；大 sprite（如 83EXET spriteW≈1544）
// 按需拉到 2048，避免 text/logo 细节因降采样变模糊。4MV9P4 挂钟那种 2D distort 视图
// 不走这条路径（perspective_mesh 直接用用户 2D 设计 base64），不受影响
const HICUSTOM_3D_RENDER_MIN = 1024;
const HICUSTOM_3D_RENDER_MAX = 2048;
const DISPLAY_SCALE = 1024 / 600;
export const hicustom3dRenderSize = (d3dSize: number | undefined) => {
  const display = (d3dSize || 600) * DISPLAY_SCALE;
  return Math.min(
    Math.max(Math.round(display), HICUSTOM_3D_RENDER_MIN),
    HICUSTOM_3D_RENDER_MAX,
  );
};

export const renderHicustomOffscreen = async (
  opts: IRenderHicustomOptions,
): Promise<string> => {
  const { textureSrc, zipfiles, uv_list, primary_uv_name, render_params } =
    opts;
  const outW = Math.max(1, Math.round(opts.width));
  const outH = Math.max(1, Math.round(opts.height));

  const glbUrl = pickMainGlbUrl(zipfiles);
  if (!glbUrl) {
    throw new Error('[hicustom] no glb url in zipfiles');
  }

  const { renderer, hostCanvas } = getSharedRenderer();
  hostCanvas.width = outW;
  hostCanvas.height = outH;
  renderer.setSize(outW, outH, false);

  const scene = new Scene();
  // envMap 在 GLB 载入后 per-material 赋值（见下面），这里先不设 scene.environment
  // —— hicustom chunk-vendors 也是走 `material.envMap = texture` 每个 mesh 单独挂
  // FOV=45 对齐 hicustom chunk-vendors.js: `this.camera = new Ge(45, n/i, .1, 1e4)`。
  // web-zhiwen 参考项目用 75，历史上靠 zoom/0.532 ≈ ×1.88 补偿（tan(75/2)/tan(45/2)
  // = 0.767/0.414 ≈ 1.85），但 zoom 补偿会连带改变透视失真 / 深度观感，跟 hicustom
  // 真实输出不一致。直接改 FOV 最干净
  const camera = new PerspectiveCamera(45, outW / outH, 0.1, 1000);

  // 相机位置用 detail_3d.position（opts.cameraPosition），不是 render_params.position
  const camPos = opts.cameraPosition || render_params.position;
  if (camPos) {
    camera.position.set(
      toNumber(camPos.x),
      toNumber(camPos.y),
      toNumber(camPos.z, 1),
    );
  } else {
    camera.position.set(0, 0, 1);
  }
  // 直接用 render_params.zoom（跟 hicustom 对齐）
  const rawZoom = toNumber(render_params.zoom, 1);
  camera.zoom = rawZoom > 0 ? rawZoom : 1;
  camera.lookAt(0, 0, 0);
  camera.updateProjectionMatrix();
  scene.add(camera);

  setupLightsFromRenderParams(scene, render_params);

  // 每次渲染都显式设 clear 透明（getSharedRenderer 里设过一次，但怕共享状态被其它代码篡改）
  renderer.setClearColor(0x000000, 0);

  const root = await getGlb(glbUrl);
  // 模型自身的旋转用 render_params.rotation（参考 web-zhiwen update3d）
  const modelRot = render_params.rotation;
  if (modelRot) {
    root.rotation.set(
      toNumber(modelRot.x),
      toNumber(modelRot.y),
      toNumber(modelRot.z),
    );
  }
  scene.add(root);

  const disposables: Texture[] = [];

  // 1) 从 zipfiles 加载材质配置（base map/normal map/opacity/scale），没这步其它材质全默认灰白
  await applyModelMaterials(root, zipfiles, disposables);
  // 1.2) 严格按 hicustom `1==S && c.env={envMap}` 只在 enable_hdr===1 时挂 env。
  // offscreen 路径用 sharedRenderer，PMREM 同 context 安全
  if (opts.enable_hdr === 1) {
    const envMap = await getEnvTexture(renderer, render_params.mainPointType);
    applyEnvMapPerMaterial(root, envMap);
  }
  // 1.5) 应用 hicustom colors_data.texture_map（如金色装饰冠的金色花纹）。
  // 对齐 hicustom vendor `c[t].map = u`，赋给所有 _base 材质
  if (opts.textureMap) {
    await applyTextureMap(root, opts.textureMap, disposables);
  }
  // 2) 给 `_base` 材质刷产品底色：RGB code 强制基色 + url 叠布料细节
  if (opts.designColorSrc || opts.designColorCode) {
    await applyDesignColor(
      root,
      opts.designColorSrc,
      opts.designColorCode,
      disposables,
    );
  }

  // 2.5) allowAlpha!=1 时隐藏非 `_custom/_base` 的 BLEND 透明 mesh（alpha<1）：
  // BDVMIC 亚克力夜灯 GLB 有 5 层玻璃装饰（alpha 0.35/0.66/0.9），叠在 _custom
  // 上面对设计堆雾化。hicustom 不走实时 3D，他们用 detail.texture 预烘焙产品照
  // 盖顶；我们走实时 3D，需要把"覆盖在设计上的玻璃层"剔掉。
  //
  // 但 4MV9P4 挂钟 P9 GLB 在 _A_custom（钟面）之外多两个半透 mesh（`_02_scale02&02`
  // alpha 0.784、`_13` alpha 0.811）渲染底座银脚 —— 跟 _custom 在 Y 方向不重叠，
  // 不该被无脑剔掉。判定改成"半透 mesh 的 bbox 与 _custom bbox 相交才隐藏"：
  // 玻璃层一定叠在设计上 → 相交 → 隐藏；底座/挂钩/支架这种边缘装饰 → 不相交 → 保留
  //
  // BLEND/transparent mesh 处理：尝试过完全不剔（跟 hicustom 官网代码一致）+
  // 修复材质字段（normalScale.y、metallicFactor、roughnessFactor、
  // metallicRoughnessTexture）但 BDVMIC 玻璃夜灯还是会雾化（我们渲染管线某些
  // 细节跟官网仍有差异）。退回基于 _custom bbox 体积重叠的启发式：
  // BDVMIC 玻璃完全包住 _custom → ratio≈1 → 隐藏；4MV9P4 P9 银脚在 _custom
  // 外 → ratio=0 → 保留。doubleSided 的 BLEND 层（7QSSMR 披风、HN5V75 玻璃挂件）
  // 是视觉特征本身，不剔
  //
  // 7A28IZ 抱毯 P3 的缝线是 BLEND mesh、跟 _custom 同面（覆盖整张毯子表面）：
  // overlapVol/customVol≈1 单看跟玻璃夜灯一样会被剔。再加 `outsideRatio`：
  // mesh bbox 落在 _custom 外的体积比。BDVMIC 玻璃罩比 _custom 大一圈 →
  // outsideRatio 高 → 仍隐藏；7A28IZ 缝线 mesh ≈ _custom bbox →
  // outsideRatio≈0 → 保留
  const spriteAllowAlpha =
    (render_params as any)?.allowAlpha === 1 ||
    (render_params as any)?.allowAlpha === '1';
  if (!spriteAllowAlpha) {
    const customBox = new Box3();
    let foundCustom = false;
    root.traverse((obj) => {
      const m = obj as Mesh;
      if (!m.isMesh || !m.material) return;
      const mat = Array.isArray(m.material)
        ? (m.material[0] as MeshStandardMaterial)
        : (m.material as MeshStandardMaterial);
      if (mat?.name?.endsWith('_custom')) {
        const subBox = new Box3().setFromObject(m);
        customBox.union(subBox);
        foundCustom = true;
      }
    });
    root.traverse((obj) => {
      const m = obj as Mesh;
      if (!m.isMesh || !m.material) return;
      const mat = Array.isArray(m.material)
        ? (m.material[0] as MeshStandardMaterial)
        : (m.material as MeshStandardMaterial);
      if (!mat?.name) return;
      if (mat.name.endsWith('_custom') || mat.name.endsWith('_base')) return;
      if (mat.side === DoubleSide) return;
      if (!mat.transparent || (mat.opacity ?? 1) >= 1) return;
      if (!foundCustom) {
        m.visible = false;
        return;
      }
      const meshBox = new Box3().setFromObject(m);
      const ox = Math.max(
        0,
        Math.min(meshBox.max.x, customBox.max.x) -
          Math.max(meshBox.min.x, customBox.min.x),
      );
      const oy = Math.max(
        0,
        Math.min(meshBox.max.y, customBox.max.y) -
          Math.max(meshBox.min.y, customBox.min.y),
      );
      const oz = Math.max(
        0,
        Math.min(meshBox.max.z, customBox.max.z) -
          Math.max(meshBox.min.z, customBox.min.z),
      );
      const overlapVol = ox * oy * oz;
      const cDx = customBox.max.x - customBox.min.x;
      const cDy = customBox.max.y - customBox.min.y;
      const cDz = customBox.max.z - customBox.min.z;
      const customVol = cDx * cDy * cDz;
      const mDx = meshBox.max.x - meshBox.min.x;
      const mDy = meshBox.max.y - meshBox.min.y;
      const mDz = meshBox.max.z - meshBox.min.z;
      const meshVol = mDx * mDy * mDz;
      const outsideRatio =
        meshVol > 0 ? (meshVol - overlapVol) / meshVol : 1;
      if (
        customVol > 0 &&
        overlapVol / customVol > 0.5 &&
        outsideRatio > 0.3
      ) {
        m.visible = false;
      }
    });
  }

  applyCoplanarOpaquePolygonOffset(root);

  {
    // 袜子 pos=18 这类 180° 翻转视图不需要额外 gate —— 下面 `materialHasMap`
    // 已经保护到：袜子内衬的 _custom 材质自带 zipfile 硅胶贴图 → materialHasMap=true
    // → 跳过应用设计。HN5V75 玻璃挂件 back 视角的 _custom 材质无默认 map →
    // materialHasMap=false → 正常贴设计（透过玻璃看到镜像设计，跟 hicustom 一致）
    // composite 底色 —— 对齐 hicustom chunk-vendors @497312：
    //   designColorType=1 → tone color；allowAlpha≠1 → "#fff"；allowAlpha=1 → 透明
    // HN5V75 玻璃挂件 allowAlpha=1 → 要透明（让玻璃 mesh 和 envmap 反射成
    // 玻璃质感，不能用白色挡住）；JS3TPD 按扣 allowAlpha 未设 → 走白底
    //
    // 之前对单 _custom mesh + 无 UV base + 非 allowAlpha=1 兜 undefined（让
    // alphaTest discard 整 mesh 露出 detail.image），但 44EBO6 袜子 P11/P12
    // detail.image 是带样品设计的市场宣传照（"Dream!"/"C"）→ 默认就漏出宣传图。
    // hicustom 官方不加 alphaTest，靠 _custom 材质 PBR baseColor 默认白渲成白底。
    // 改成统一白底，CX75VA 红帽 sprite 实测无回归
    const allowAlpha =
      (render_params as any)?.allowAlpha === 1 ||
      (render_params as any)?.allowAlpha === '1';
    const compositeRgb =
      opts.designColorCode && opts.designColorCode.length >= 3
        ? opts.designColorCode
        : allowAlpha
        ? undefined
        : ['255', '255', '255'];
    const fallbackSrc =
      compositeRgb && compositeRgb.length >= 3
        ? makeBlankCanvasDataUrl(64, 64, compositeRgb)
        : opts.designColorSrc || '';

    // 3) 主 uv：
    //    - 材质已有 zipfiles map（硅胶/菱格内衬等独立贴图） → 保留，不覆盖
    //    - 其它一律走 composite → applyTextureToMaterial
    //
    // 默认预览（textureSrc = HICUSTOM_INIT_TEXTURE，1×1 透明）和空保存
    // （textureSrc = Pixi 全透截图 2000×2000）在 composite 内视觉等价：
    // drawImage 全透 src → canvas 保留 compositeRgb 底色 → 纯色输出。
    // 走同一条路径保证两种场景下 _custom 材质状态完全一致（同
    // alphaTest/alphaToCoverage/map），避免"默认预览和空保存渲出来不一样"
    // 的肉眼可见差异
    // compositeSize 随输出分辨率走，保真度跟最终 3D 图一致
    const compositeSize = Math.max(outW, outH);
    const primaryUvData = uv_list.find((u) => u.name === primary_uv_name);
    const primaryFace = primaryUvData?.face_list?.[0];
    if (!materialHasMap(root, primary_uv_name)) {
      if (textureSrc) {
        // textureSrc 是 ComponentScene 的完整 face canvas 截图（含 print_area
        // 外 whitespace）。compositeDesignOnColor 按 face.params 从 print_area
        // 区域裁出铺满 texture canvas，匹配 hicustom 贴图（app.js line 5880）
        const composited = await compositeDesignOnColor(
          textureSrc,
          compositeRgb,
          compositeSize,
          primaryFace,
        );
        const primaryTex = await loadTextureFromSrc(composited);
        disposables.push(primaryTex);
        applyTextureToMaterial(root, primary_uv_name, primaryTex);
      } else if (fallbackSrc) {
        const primaryTex = await loadTextureFromSrc(fallbackSrc, {
          flipY: false,
        });
        disposables.push(primaryTex);
        applyTextureToMaterial(root, primary_uv_name, primaryTex);
      }
    }

    // 其他 uv：同样的规则。同 view 的伴随 UV 直接复用 textureSrc（主图）—
    // NBYNSA 拖鞋 GLB 把鞋底面 / 鞋帮拆成两个 UV (`_03_..._custom` 和
    // `_A_..._custom`) 但都映射到 view 1，hicustom 把同一份设计贴到两个 mesh
    // 上才能铺满整个鞋面；ComponentScene 收集的 otherUVTextures 跳过了
    // `vid === primary_view_id`，这条分支直接命中 textureSrc 兜回来
    const primaryViewId = primaryFace?.view_id;
    for (const uv of uv_list) {
      if (uv.name === primary_uv_name) continue;
      const face = uv.face_list?.[0];
      const viewId = face?.view_id;
      if (viewId == null) continue;
      if (materialHasMap(root, uv.name)) continue;
      const userSrc =
        viewId === primaryViewId
          ? textureSrc
          : opts.otherUVTextures?.[viewId];
      if (userSrc) {
        const composited = await compositeDesignOnColor(
          userSrc,
          compositeRgb,
          compositeSize,
          face,
        );
        const tex = await loadTextureFromSrc(composited);
        disposables.push(tex);
        applyTextureToMaterial(root, uv.name, tex);
      } else if (fallbackSrc) {
        const tex = await loadTextureFromSrc(fallbackSrc, { flipY: false });
        disposables.push(tex);
        applyTextureToMaterial(root, uv.name, tex);
      }
    }
  }
  // 不主动遍历 root 去给所有 _custom 材质刷色——只处理当前 sprite uv_list 里列出的那些，
  // 避免抹掉模型上别的 _custom 材质（比如某些型号的硅胶/菱格内衬面）。

  renderer.render(scene, camera);
  const dataURL = hostCanvas.toDataURL('image/png');

  // 清理：renderer / envTexture 是共享的，不 dispose；只清这次渲染的私有资源
  scene.remove(root);
  disposables.forEach((t) => t.dispose());
  root.traverse((obj) => {
    const m = obj as Mesh;
    if (!m.isMesh) return;
    const mat = m.material as MeshStandardMaterial | MeshStandardMaterial[];
    if (Array.isArray(mat)) {
      mat.forEach((mm) => mm.dispose?.());
    } else {
      mat?.dispose?.();
    }
    m.geometry?.dispose?.();
  });

  return dataURL;
};

/**
 * 跟 `renderHicustomOffscreen` 完全相同的材质 / camera / scene 管线，但挂到外部
 * DOM 容器上 + 加 OrbitControls，用于 dev 模式调试 — 当离屏 dataURL 渲染结果
 * 跟 3D 模式不一致时（H2PXXM preview3 这类），可以转视角看实际 mesh 结构。
 *
 * 调用方负责：
 *   - 容器 size 确定（function 用容器 clientWidth/Height 设 renderer + camera 比例）
 *   - 拿到返回的 `dispose()` 在 unmount 时调用，否则会泄漏 WebGL context
 */
export const mountHicustomOffscreenDebug3d = async (
  container: HTMLDivElement,
  opts: IRenderHicustomOptions,
): Promise<{ dispose: () => void; root: Object3D }> => {
  const { textureSrc, zipfiles, uv_list, primary_uv_name, render_params } =
    opts;
  const w = Math.max(1, container.clientWidth || 600);
  const h = Math.max(1, container.clientHeight || 600);

  const canvas = document.createElement('canvas');
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  container.appendChild(canvas);

  const renderer = new WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
  });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(w, h, false);
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.setClearColor(0x222222, 1);

  const scene = new Scene();
  const camera = new PerspectiveCamera(45, w / h, 0.1, 1000);
  const camPos = opts.cameraPosition || render_params.position;
  if (camPos) {
    camera.position.set(
      toNumber(camPos.x),
      toNumber(camPos.y),
      toNumber(camPos.z, 1),
    );
  } else {
    camera.position.set(0, 0, 1);
  }
  const rawZoom = toNumber(render_params.zoom, 1);
  camera.zoom = rawZoom > 0 ? rawZoom : 1;
  camera.lookAt(0, 0, 0);
  camera.updateProjectionMatrix();
  scene.add(camera);

  setupLightsFromRenderParams(scene, render_params);

  const glbUrl = pickMainGlbUrl(zipfiles);
  if (!glbUrl) throw new Error('[hicustom debug] no glb url');
  const root = await getGlb(glbUrl);
  const modelRot = render_params.rotation;
  if (modelRot) {
    root.rotation.set(
      toNumber(modelRot.x),
      toNumber(modelRot.y),
      toNumber(modelRot.z),
    );
  }
  scene.add(root);

  const disposables: Texture[] = [];
  await applyModelMaterials(root, zipfiles, disposables);
  if (opts.enable_hdr === 1) {
    const envMap = await getEnvTexture(renderer, render_params.mainPointType);
    applyEnvMapPerMaterial(root, envMap);
  }
  if (opts.textureMap) {
    await applyTextureMap(root, opts.textureMap, disposables);
  }
  if (opts.designColorSrc || opts.designColorCode) {
    await applyDesignColor(
      root,
      opts.designColorSrc,
      opts.designColorCode,
      disposables,
    );
  }
  applyCoplanarOpaquePolygonOffset(root);

  // _custom 走主 uv + 其他 uv 同 renderHicustomOffscreen 的逻辑
  const allowAlpha =
    (render_params as any)?.allowAlpha === 1 ||
    (render_params as any)?.allowAlpha === '1';
  const compositeRgb =
    opts.designColorCode && opts.designColorCode.length >= 3
      ? opts.designColorCode
      : allowAlpha
      ? undefined
      : ['255', '255', '255'];
  const fallbackSrc =
    compositeRgb && compositeRgb.length >= 3
      ? makeBlankCanvasDataUrl(64, 64, compositeRgb)
      : opts.designColorSrc || '';
  const compositeSize = 2048;
  const primaryUvData = uv_list.find((u) => u.name === primary_uv_name);
  const primaryFace = primaryUvData?.face_list?.[0];
  if (!materialHasMap(root, primary_uv_name)) {
    if (textureSrc) {
      const composited = await compositeDesignOnColor(
        textureSrc,
        compositeRgb,
        compositeSize,
        primaryFace,
      );
      const tex = await loadTextureFromSrc(composited);
      disposables.push(tex);
      applyTextureToMaterial(root, primary_uv_name, tex);
    } else if (fallbackSrc) {
      const tex = await loadTextureFromSrc(fallbackSrc, { flipY: false });
      disposables.push(tex);
      applyTextureToMaterial(root, primary_uv_name, tex);
    }
  }
  const primaryViewId = primaryFace?.view_id;
  for (const uv of uv_list) {
    if (uv.name === primary_uv_name) continue;
    const face = uv.face_list?.[0];
    const viewId = face?.view_id;
    if (viewId == null) continue;
    if (materialHasMap(root, uv.name)) continue;
    const userSrc =
      viewId === primaryViewId ? textureSrc : opts.otherUVTextures?.[viewId];
    if (userSrc) {
      const composited = await compositeDesignOnColor(
        userSrc,
        compositeRgb,
        compositeSize,
        face,
      );
      const tex = await loadTextureFromSrc(composited);
      disposables.push(tex);
      applyTextureToMaterial(root, uv.name, tex);
    } else if (fallbackSrc) {
      const tex = await loadTextureFromSrc(fallbackSrc, { flipY: false });
      disposables.push(tex);
      applyTextureToMaterial(root, uv.name, tex);
    }
  }

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(0, 0, 0);
  controls.update();

  let raf = 0;
  const tick = () => {
    controls.update();
    renderer.render(scene, camera);
    raf = requestAnimationFrame(tick);
  };
  tick();

  const onResize = () => {
    const nw = container.clientWidth || w;
    const nh = container.clientHeight || h;
    renderer.setSize(nw, nh, false);
    camera.aspect = nw / nh;
    camera.updateProjectionMatrix();
  };
  const ro = new ResizeObserver(onResize);
  ro.observe(container);

  return {
    root,
    dispose: () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      scene.remove(root);
      disposables.forEach((t) => t.dispose());
      root.traverse((obj) => {
        const m = obj as Mesh;
        if (!m.isMesh) return;
        const mat = m.material as MeshStandardMaterial | MeshStandardMaterial[];
        if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose?.());
        else mat?.dispose?.();
        m.geometry?.dispose?.();
      });
      renderer.dispose();
      canvas.remove();
    },
  };
};

// 初始（无设计）时用来铺在可定制材质上的纯色底图。按 design_color_code 填，保证
// 把用户设计（可能大片透明）叠在 designColor 实色底之上。必要性：
//   _custom 材质上了 alphaTest=0.1（见 applyTextureToMaterial），任何 alpha=0 的
//   像素被 discard。我们又强制 side=DoubleSide（65ZQ77 等单面 GLB 需要背面可见），
//   结果就是设计里透明的区域会从前面穿透看到模型背面（4MV9P4 Preview 8 露出黑色
//   挂钟背板五边形）。兜一层 opaque designColor，贴图处处不透明 → alphaTest 不再
//   discard 出空洞，跟 hicustom 官方行为一致
// face 的 params 定义了用户设计在 UV 贴图上的位置 / 缩放。如 65ZQ77:
//   face.width/height = 1200, params = { left: 116, top: 114, scaleX: 0.81, scaleY: 0.81 }
// → 设计缩到 972×972，居中偏移 116,114 放在 1200 画布中。4 周各 ~10% 留白
// 大多数产品 scale=0.97（3% 留白），65ZQ77 是 0.81 特别明显
interface IHicustomFaceParams {
  left?: number;
  top?: number;
  scaleX?: number;
  scaleY?: number;
}

const compositeDesignOnColor = async (
  designUrl: string,
  rgb: string[] | undefined,
  size: number,
  face?: {
    width?: number;
    height?: number;
    params?: IHicustomFaceParams;
  },
): Promise<string> => {
  // 无 rgb 且没 face 参数：没有可落位信息，直接返回（texture 当作铺满 [0,1] UV 用）
  if ((!rgb || rgb.length < 3) && !face?.params) return designUrl;
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = designUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(size));
    canvas.height = Math.max(1, Math.round(size));
    const ctx = canvas.getContext('2d');
    if (!ctx) return designUrl;
    // 有 rgb 刷底色填满 canvas；没 rgb 留透明（UV 外的区域靠 alphaTest discard，
    // 不会采到设计边外的像素产生平铺 —— CX75VA 红帽子就是这种 rgb=undefined 路径）
    if (rgb && rgb.length >= 3) {
      const r = toNumber(rgb[0], 255);
      const g = toNumber(rgb[1], 255);
      const b = toNumber(rgb[2], 255);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    // input designUrl 是 ComponentScene screenshot（option C 后 = print_area 尺寸
    // 非正方形）。按 face.params 把它放到 canvas 的 print_area 位置 → texture
    // 四周留 designColor 边距，匹配 hicustom（design 在模型面占 81%，不是 100%；
    // GLB UV 采 [0,1] 会同时采到 designColor 边和中央 design）。
    //
    // face.width/height = UV 参考尺寸，face.params.left/top = design 在 face
    // canvas 的 px 偏移，scaleX/Y = design 占 face 的比例。
    // 关键：source 长宽比要保留 —— face.params 是按 hicustom 自己的方形 source
    // 定的；option C 后我们的 source 是 print_area 比例（CX75VA 2048×1280），
    // 直接按 scaleX×canvas × scaleY×canvas 填会非均匀拉伸造成 3D 渲染变形，
    // 必须在目标区内做等比 letterbox，短边居中留透明
    const fw = face?.width;
    const fh = face?.height;
    const p = face?.params;
    if (fw && fh && p) {
      const kx = canvas.width / fw;
      const ky = canvas.height / fh;
      const sx = toNumber(p.scaleX, 1);
      const sy = toNumber(p.scaleY, 1);
      const targetLeft = toNumber(p.left, 0) * kx;
      const targetTop = toNumber(p.top, 0) * ky;
      const targetW = canvas.width * sx;
      const targetH = canvas.height * sy;
      // 等比 fit 到目标区（contain 模式）
      const srcAspect = img.width / img.height;
      const tgtAspect = targetW / targetH;
      let dw = targetW;
      let dh = targetH;
      if (srcAspect > tgtAspect) {
        // source 比目标区更宽：以目标区宽为准，高度等比缩
        dh = targetW / srcAspect;
      } else {
        dw = targetH * srcAspect;
      }
      const dx = targetLeft + (targetW - dw) / 2;
      const dy = targetTop + (targetH - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
    } else {
      // 没 face 信息就铺满（非 hicustom 或无 print_area）
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }
    return canvas.toDataURL('image/png');
  } catch (e) {
    console.warn('[hicustom] compositeDesignOnColor failed', e);
    return designUrl;
  }
};

// "没设计时" 可定制区域和 _base 区域视觉一致（黑色款全黑、白色款全白）
const makeBlankCanvasDataUrl = (w: number, h: number, rgb?: string[]) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w));
  canvas.height = Math.max(1, Math.round(h));
  const ctx = canvas.getContext('2d');
  if (ctx) {
    if (rgb && rgb.length >= 3) {
      const r = toNumber(rgb[0], 255);
      const g = toNumber(rgb[1], 255);
      const b = toNumber(rgb[2], 255);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
    } else {
      ctx.fillStyle = '#ffffff';
    }
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  return canvas.toDataURL('image/png');
};

/**
 * 为 hicustom 产品的每个 sprite 预生成一次 3D 投影贴图，避免初始展示的是
 * 平铺的 UV 面板模板（视觉上往往为黑/错位）。已被用户保存过的贴图（data:）不覆盖。
 */
export const renderAllHicustomInitial = async (product: IProduct) => {
  // 产品级 modelColor/Code 不再被 per-sprite 渲染使用（见下面 spriteHasUvBase
  // 逻辑）；textureMap 仍然是整模型共用，保留
  const modelTextureMap = (product.userData as any)?.hicustomModelTextureMap as
    | string
    | undefined;
  for (const part of product.parts) {
    for (const template of part.templates) {
      const scene = product.scenes[template.scene_id];
      if (!scene) continue;
      for (const comp of scene.components) {
        const hc = (comp.props.userData as any)?.hicustom;
        if (!hc) continue;
        // PerspectiveMesh 不走 3D 离屏渲染，texture 直接存用户 2D 设计
        if (comp.type === 'perspective_mesh') continue;
        // 只渲染主 uv 的 sprite，非主 uv 的伴随 sprite 只用于挂 view 入口（invisible）
        if (hc.is_primary_uv === false) continue;
        const currentTex = (comp.props as any).texture as string | undefined;
        // 用户保存的设计是 data URL，跳过不覆盖；产品初始占位也是 data URL
        //（HICUSTOM_INIT_TEXTURE 1×1 透明 PNG），需要继续渲真图
        if (
          currentTex &&
          currentTex.startsWith('data:') &&
          currentTex !== HICUSTOM_INIT_TEXTURE
        ) {
          continue;
        }
        const w = hicustom3dRenderSize(hc.width);
        const h = hicustom3dRenderSize(hc.height);
        try {
          // 有 modelColor 兜底色（dark tone1 或 colors_data 提供的 url/code）：
          //   textureSrc='' → 走 fallbackSrc 把 _custom 材质刷成底色（65ZQ77 黑
          //   信箱套、44EBO6 灰袜子等需要面料可见）
          // 无（亮色 / 无色的玻璃/亚克力产品如 83EXET）：
          //   textureSrc=HICUSTOM_INIT_TEXTURE → alphaTest=0.5 discard 掉 mesh，
          //   让 detail.image + detail.texture 露出来
          // 是否给 sprite 的 _custom 材质刷底色，完全由 sprite 自己的 uv base 决定：
          // 有 url/code 就刷（65ZQ77 黑信箱套）；空就不刷，让 alphaTest discard 掉
          // mesh 露出 detail.image（CX75VA 红帽 pos=24 设计块，hicustom 不上色）。
          // 不再回退到产品级 modelColor/tone —— 产品级兜底是给"整体模型底色"用的
          // （如 HN5V75 金属挂件的金色贴图），per-uv 空就是 hicustom 的明确声明
          const uvBase = hc.primary_design_color_custom_base || {};
          const spriteHasUvBase = !!(
            uvBase.design_color_url ||
            (uvBase.design_color_code || []).length >= 3
          );
          const initialTextureSrc = spriteHasUvBase ? '' : HICUSTOM_INIT_TEXTURE;
          const dataUrl = await renderHicustomOffscreen({
            textureSrc: initialTextureSrc,
            zipfiles: hc.zipfiles || [],
            uv_list: hc.uv_list || [],
            primary_uv_name: hc.primary_uv_name || '',
            render_params: hc.render_params || {},
            cameraPosition: hc.position,
            designColorSrc: spriteHasUvBase ? uvBase.design_color_url : '',
            designColorCode: spriteHasUvBase ? uvBase.design_color_code : undefined,
            textureMap: modelTextureMap,
            width: w,
            height: h,
            otherUVTextures: {},
            enable_hdr: hc.enable_hdr,
          });
          (comp.props as any).texture = dataUrl;
        } catch (e) {
          console.error(
            '[hicustom] initial render failed',
            hc.detail_position,
            hc.part_index,
            e,
          );
        }
      }
    }
  }
};

/**
 * 针对单个 view 重渲所有受影响 sprite 的 3D 合成贴图。
 * ComponentScene.clickDone 和 switchHicustomColor 共用，避免逻辑重复。
 * 同一 view_id 在不同 detail 可能有多个 sprite（主 uv + 伴随 uv），都刷新。
 */
export const rerenderHicustomForView = async (
  product: IProduct,
  viewId: number,
  opts?: {
    // 可选覆盖的 textureMap（切色时用新色），默认取 product.userData
    modelTextureMap?: string;
    // 可选 store2d 引用，命中"当前 scene 的 comp"时走 updateComponent（触发 pixi 重渲），
    // 其它 scene 直接 mutate comp.props.texture
    onCompUpdate?: (compId: string, texture: string) => void | Promise<void>;
  },
) => {
  // 产品级 modelColor/Code 不再被 per-sprite 渲染使用；textureMap 仍整模型共用
  const modelTextureMap =
    opts?.modelTextureMap ??
    ((product.userData as any)?.hicustomModelTextureMap as string | undefined);
  for (const part of product.parts) {
    const template = part.templates.find(
      (t) => t.id === part.currentTemplateId,
    );
    if (!template) continue;
    const scene = product.scenes[template.scene_id];
    if (!scene) continue;
    for (const comp of scene.components) {
      const hc = (comp.props as any).userData?.hicustom;
      if (!hc) continue;
      if (hc.is_primary_uv === false) continue;

      // 纯 2D distort 视图（hicustom type=0，无 detail_3d）：PerspectiveMesh 按
      // 4 角把目标 view 的 2D 设计扭到这个视角，不走 3D 离屏渲染
      if (comp.type === 'perspective_mesh' && hc.target_view_id === viewId) {
        const userDesign =
          getHicustomFaceDesign(viewId) || HICUSTOM_DISTORT_INIT_TEXTURE;
        if (opts?.onCompUpdate) {
          await opts.onCompUpdate(comp.id, userDesign);
        } else {
          (comp.props as any).texture = userDesign;
        }
        continue;
      }

      const uvList: any[] = hc.uv_list || [];
      const hit = uvList.some((uv) => uv.face_list?.[0]?.view_id === viewId);
      if (!hit) continue;
      const otherUVTextures: Record<number, string> = {};
      for (const uv of uvList) {
        const vid = uv.face_list?.[0]?.view_id;
        if (vid == null || vid === hc.primary_view_id) continue;
        const cached = getHicustomFaceDesign(vid);
        if (cached) otherUVTextures[vid] = cached;
      }
      const userDesign = getHicustomFaceDesign(hc.primary_view_id);
      // 有用户设计用用户设计；否则传空字符串（renderHicustomOffscreen 会保留
      // zipfiles 自带贴图或刷底色，跟 renderAllHicustomInitial 行为一致）
      const textureSrc = userDesign || '';
      // 同 renderAllHicustomInitial：sprite 的 _custom 材质刷不刷底色看 sprite 自己
      // 的 primary_design_color_custom_base，空就不刷（不回退到产品级 tone）
      const uvBase = hc.primary_design_color_custom_base || {};
      const spriteHasUvBase = !!(
        uvBase.design_color_url ||
        (uvBase.design_color_code || []).length >= 3
      );
      try {
        const dataUrl = await renderHicustomOffscreen({
          textureSrc,
          zipfiles: hc.zipfiles || [],
          uv_list: hc.uv_list || [],
          primary_uv_name: hc.primary_uv_name || '',
          render_params: hc.render_params || {},
          cameraPosition: hc.position,
          designColorSrc: spriteHasUvBase ? uvBase.design_color_url : '',
          designColorCode: spriteHasUvBase ? uvBase.design_color_code : undefined,
          textureMap: modelTextureMap,
          width: hicustom3dRenderSize(hc.width),
          height: hicustom3dRenderSize(hc.height),
          otherUVTextures,
          enable_hdr: hc.enable_hdr,
        });
        if (opts?.onCompUpdate) {
          await opts.onCompUpdate(comp.id, dataUrl);
        } else {
          (comp.props as any).texture = dataUrl;
        }
      } catch (e) {
        console.error(
          '[hicustom] rerender for view failed',
          hc.detail_position,
          hc.part_index,
          e,
        );
      }
    }
  }
};

interface ISwitchHicustomColorOpts {
  onProgress?: (done: number, total: number) => void;
}

/**
 * 切换 hicustom 产品的颜色。保留用户设计（subScene 不动），只：
 * 1) 更新 product.userData 的 modelColorUrl/Code + currentColorId
 * 2) swap 每个 scene 的 base / texture_overlay / effect_image sprite 的 texture
 *    到新色的 detail.image/texture/effect_image
 * 3) 对每个 unique view_id 调 rerenderHicustomForView 重渲 3D 合成贴图
 *
 * 3D 交互视图：调用方（Design2d）感知 product.userData.hicustomCurrentColorId 变化
 * 后重新 mountHicustom3d 即可应用新 modelColor。
 */
export const switchHicustomColor = async (
  product: IProduct,
  colorId: number,
  opts?: ISwitchHicustomColorOpts,
) => {
  const ud = product.userData as any;
  const colors = ud?.hicustomColors as
    | Array<{
        id: number;
        name: string;
        tone1: string;
        tone2: string;
        detailsByPosition: Record<
          number,
          {
            image?: string;
            texture?: string;
            effect_image?: string;
            parts: Array<{ distort_params: any; detail_3d: any }>;
          }
        >;
        modelColorUrl: string;
        modelColorCode: string[];
        textureMap?: string;
        modelData: any;
      }>
    | undefined;
  if (!Array.isArray(colors)) return;
  const next = colors.find((c) => c.id === colorId);
  if (!next) return;

  // 1) userData：模型底色 + 当前色 model_data + 当前色标识
  //    hicustomModelData 必须随色切：7BJT8H 双联开关每色对应不同 GLB（拨杆 vs
  //    Decora，白块大小不同）。product.ts 里 modelData 已三段优先匹配
  //    （color_id → colors_data.some → [0] 兜底），永远 truthy；HN5V75/JS3TPD
  //    这类多色共享 GLB 也走兜底拿同一 model_data，写回也是同值无副作用
  ud.hicustomModelColorUrl = next.modelColorUrl;
  ud.hicustomModelColorCode = next.modelColorCode;
  ud.hicustomModelTextureMap = next.textureMap || '';
  if (next.modelData) ud.hicustomModelData = next.modelData;
  ud.hicustomCurrentColorId = next.id;
  ud.colorId = next.id;
  ud.colorName = next.name;
  ud.colorTone1 = next.tone1;
  ud.colorTone2 = next.tone2;

  // 2) 逐 scene 更新：
  //    a) base / texture_overlay / effect_image sprite 的 texture 换新色预渲图
  //    b) hicustom sprite 的 userData.hicustom 按 part_index + uv_index 对齐到新色
  //       detail_3d，替换 zipfiles / render_params / position / uv_list / face_list /
  //       enable_hdr / distort_params / primary_uv_name / primary_design_color_custom_base
  //       —— 保证后续 renderHicustomOffscreen 用新色的 GLB 和材质数据
  for (const part of product.parts) {
    const position = (part.userData as any)?.detail_position as
      | number
      | undefined;
    if (position == null) continue;
    const detail = next.detailsByPosition[position];
    if (!detail) continue;
    for (const template of part.templates) {
      const scene = product.scenes[template.scene_id];
      if (!scene) continue;
      for (const comp of scene.components) {
        const name = (comp.props as any).name;
        const hc = (comp.props as any).userData?.hicustom;
        if (name === 'base' && detail.image !== undefined) {
          (comp.props as any).texture = detail.image || '';
        } else if (name === 'texture_overlay' && detail.texture !== undefined) {
          (comp.props as any).texture = detail.texture || '';
          (comp.props as any).visible = !!detail.texture;
        } else if (
          name === 'effect_image' &&
          detail.effect_image !== undefined
        ) {
          (comp.props as any).texture = detail.effect_image || '';
          (comp.props as any).visible = !!detail.effect_image;
        } else if (hc) {
          const nextPart = detail.parts?.[hc.part_index];
          if (!nextPart?.detail_3d) continue;
          const nd3d = nextPart.detail_3d;
          const nextUv = nd3d.uv_list?.[hc.uv_index];
          const nextFacePrimary =
            nextUv?.face_list?.[0] || nd3d.face_list?.[0];
          // patch per-color 的 3D 相关字段；保留 part_index/uv_index/primary_view_id
          // 等"身份"字段（新色数据按位置对齐，身份不变）
          hc.zipfiles = nd3d.zipfiles;
          hc.render_params = nd3d.render_params;
          hc.position = nd3d.position;
          hc.uv_list = nd3d.uv_list;
          hc.face_list = nd3d.face_list;
          hc.enable_hdr = nd3d.enable_hdr;
          if (nextUv) {
            hc.primary_uv_name = nextUv.name;
            hc.primary_design_color_custom_base = nextUv.design_color_custom_base;
          }
          if (nextFacePrimary) {
            hc.primary_face_url = nextFacePrimary.url;
            hc.primary_face_name = nextFacePrimary.name;
            hc.primary_face_custom_name = nextFacePrimary.custom_name;
            hc.primary_face_width = nextFacePrimary.width;
            hc.primary_face_height = nextFacePrimary.height;
          }
        }
      }
    }
  }

  // 3) 批量重渲每个 view 的 3D 合成贴图
  const viewIds = new Set<number>();
  for (const part of product.parts) {
    const template = part.templates.find(
      (t) => t.id === part.currentTemplateId,
    );
    if (!template) continue;
    const scene = product.scenes[template.scene_id];
    if (!scene) continue;
    for (const comp of scene.components) {
      const hc = (comp.props as any).userData?.hicustom;
      if (!hc || hc.is_primary_uv === false) continue;
      if (hc.primary_view_id != null) viewIds.add(hc.primary_view_id);
    }
  }
  const total = viewIds.size;
  opts?.onProgress?.(0, total);
  let done = 0;
  for (const viewId of viewIds) {
    await rerenderHicustomForView(product, viewId);
    done += 1;
    opts?.onProgress?.(done, total);
  }
};

interface IMountHicustom3dOpts {
  scene: Scene;
  camera: PerspectiveCamera;
  product: IProduct;
  // envMap 需要用实际渲染这个 scene 的 renderer 做 PMREM（否则跨 GL context
  // 失效，金属/玻璃反射不生效）。Design2d 从 `core.store3d.renderer` 传入
  renderer?: WebGLRenderer;
}

/**
 * 把 hicustom 整体产品挂到一个 Three.js scene 上（供 Design2d 的 3D 预览模式用）。
 * 与 renderHicustomOffscreen 不同：
 * - 用 models.json 的 model_data（整个产品的 GLB + uv_list）；sub-part zipfiles 只管一部分网格
 * - scene/camera 是外部 use3d.mount 已经建好的（含 OrbitControls）
 * - 不创建新的 renderer，不输出截图；只往已有场景里加 light + model
 */
export const mountHicustom3d = async (opts: IMountHicustom3dOpts) => {
  const { scene, camera, product, renderer: externalRenderer } = opts;
  const modelData = (product.userData as any)?.hicustomModelData as
    | {
        position?: IPosition3;
        render_params?: IHicustomRenderParams;
        base_files?: IHicustomZipfile[];
        uv_list?: Array<{
          name: string;
          face_list?: Array<{
            view_id: number;
            width?: number;
            height?: number;
            params?: IHicustomFaceParams;
          }>;
        }>;
      }
    | undefined;
  if (!modelData) return;

  const zipfiles = modelData.base_files || [];
  const glbUrl = pickMainGlbUrl(zipfiles);
  if (!glbUrl) return;

  // 已经有上次挂的模型就先移除（切回 2D 又切 3D 不会累积）
  const prev = scene.getObjectByName('hicustom_3d_model');
  if (prev) scene.remove(prev);

  // 灯光：先清掉之前留下的光（切 2D→3D→2D→3D 不重复叠加），再加固定套路
  const oldLights = scene.children.filter(
    (c) =>
      c instanceof DirectionalLight ||
      c instanceof AmbientLight ||
      c instanceof PointLight,
  );
  for (const l of oldLights) scene.remove(l);
  setupLightsFromRenderParams(scene, modelData.render_params);

  // envMap 在 GLB 载入后 per-material 赋值（对齐 hicustom chunk-vendors 的
  // `convertEnvMap + traverse(mesh => mat.envMap = tex)` 路径）。清掉可能残留
  // 的 scene.environment，避免双重生效
  scene.environment = null;

  // 加载主 GLB
  const root = await getGlb(glbUrl);
  root.name = 'hicustom_3d_model';

  const rot = modelData.render_params?.rotation;
  if (rot) {
    root.rotation.set(toNumber(rot.x), toNumber(rot.y), toNumber(rot.z));
  }

  // 相机先到位 + root 隐身：后续 await 材质期间渲染循环不会用初始相机渲一帧"很大的模型"，
  // 全部就绪后再 visible=true，避免切到 3D 时一下"很大→闪→正常"
  const MARGIN_FACTOR = 0.8;
  const camPos = modelData.position;
  if (camPos) {
    camera.position.set(
      toNumber(camPos.x),
      toNumber(camPos.y),
      toNumber(camPos.z, 1),
    );
  }
  const rawZoom = toNumber(modelData.render_params?.zoom, 1);
  camera.zoom = (rawZoom > 0 ? rawZoom : 1) * MARGIN_FACTOR;
  camera.lookAt(0, 0, 0);
  camera.updateProjectionMatrix();

  root.visible = false;
  scene.add(root);

  const disposables: Texture[] = [];

  // 1) base_files 里的材质（硅胶 honeycomb、法线贴图等）
  await applyModelMaterials(root, zipfiles, disposables);

  // 1.2) enable_hdr=1 时给所有 mesh material 挂 envMap（对齐 hicustom 的
  // per-material envMap 路径）。**必须用外部 renderer 做 PMREM**，跨 context
  // texture 会失效
  const enableHdr = (modelData as any).enable_hdr === 1;
  if (enableHdr && externalRenderer) {
    const envMap = await getEnvTexture(
      externalRenderer,
      modelData.render_params?.mainPointType,
    );
    applyEnvMapPerMaterial(root, envMap);
  }

  // 1.5) hicustom colors_data.texture_map 给所有 _base 材质的 mat.map（金色花纹等）
  const textureMap = (product.userData as any)?.hicustomModelTextureMap as
    | string
    | undefined;
  if (textureMap) {
    await applyTextureMap(root, textureMap, disposables);
  }

  // 2) _base 材质的产品底色
  const designColorSrc = (product.userData as any)?.hicustomModelColorUrl as
    | string
    | undefined;
  const designColorCode = (product.userData as any)?.hicustomModelColorCode as
    | string[]
    | undefined;
  if (designColorSrc || designColorCode) {
    await applyDesignColor(root, designColorSrc, designColorCode, disposables);
  }
  applyCoplanarOpaquePolygonOffset(root);

  // 3) 每个 uv 的 _custom 材质按顺序：用户设计 > zipfiles 默认贴图 > 产品底色兜底
  //    compositeRgb 两分支，对齐 hicustom chunk-vendors @497312：
  //      designColorCode → tone；allowAlpha=1 → 透明（HN5V75 玻璃）；
  //      其余 → 白底（之前对单 _custom + 无 base 兜 undefined → alphaTest discard
  //      露出 detail.image，但 44EBO6 袜子 P11/P12 detail.image 是带样品设计的
  //      宣传照会漏出，跟 renderHicustomOffscreen 同步改成统一白底）
  const allowAlpha =
    (modelData.render_params as any)?.allowAlpha === 1 ||
    (modelData.render_params as any)?.allowAlpha === '1';
  const compositeRgb =
    designColorCode && designColorCode.length >= 3
      ? designColorCode
      : allowAlpha
      ? undefined
      : ['255', '255', '255'];
  let fallbackTex: Texture | undefined;
  if (compositeRgb && compositeRgb.length >= 3) {
    const fallbackUrl = makeBlankCanvasDataUrl(64, 64, compositeRgb);
    fallbackTex = await loadTextureFromSrc(fallbackUrl, { flipY: false });
    disposables.push(fallbackTex);
  }
  for (const uv of modelData.uv_list || []) {
    const face = uv.face_list?.[0];
    const viewId = face?.view_id;
    if (viewId == null) continue;
    const designUrl = hicustomFaceDesigns.get(viewId);
    if (designUrl) {
      const composited = await compositeDesignOnColor(
        designUrl,
        compositeRgb,
        2048,
        face,
      );
      const tex = await loadTextureFromSrc(composited);
      disposables.push(tex);
      applyTextureToMaterial(root, uv.name, tex);
    } else if (allowAlpha && !materialHasMap(root, uv.name)) {
      // HN5V75 玻璃挂件：给 _custom 贴一张全透 1×1 texture → alphaTest discard
      // → 玻璃层从 GLB 默认 material 渲，背后可见页面白底
      const transparentTex = await loadTextureFromSrc(HICUSTOM_INIT_TEXTURE, {
        flipY: false,
      });
      disposables.push(transparentTex);
      applyTextureToMaterial(root, uv.name, transparentTex);
    } else if (fallbackTex) {
      // 没用户设计 + 有兜底色（compositeRgb 来自 designColorCode 或默认白底）：
      // 给 _custom 贴 64×64 兜底 PNG，覆盖 GLB 默认材质。视觉与 hicustom 官网对齐：
      //  - 7SXXN4 黑色睡裤 designColorCode=[0,0,0] → _custom 变纯黑
      //  - JS3TPD 按扣 / 44EBO6 袜子 P11/P12 等 → 白 opaque 盖住底图
      applyTextureToMaterial(root, uv.name, fallbackTex);
    }
  }

  root.visible = true;
};

/**
 * Design 完单个 view 后把新设计贴到已挂载的 3D 模型对应 UV 的材质上。
 *
 * 3D-first 流程里，用户进页面就停在 3D 模式 → `mountHicustom3d` 只跑一次，
 * 读的是当时的 `hicustomFaceDesigns` 缓存。之后 ComponentScene Done 只更新
 * 2D sprite（rerenderHicustomForView），3D 模型的材质贴图不跟。必须显式把
 * 新 designUrl 塞到对应 UV 材质的 map 上，模型才会实时反映设计。
 *
 * 2D-first 不用这个函数：切 2D→3D 时 useEffect 触发 `mountHicustom3d` 重跑，
 * 自然读到最新 hicustomFaceDesigns。
 */
export const updateHicustom3dFaceTexture = async (
  scene: Scene | undefined | null,
  product: IProduct,
  viewId: number,
  designUrl: string,
) => {
  if (!scene || !designUrl) return;
  const root = scene.getObjectByName('hicustom_3d_model');
  if (!root) return;
  const modelData = (product.userData as any)?.hicustomModelData as
    | {
        uv_list?: Array<{
          name: string;
          face_list?: Array<{
            view_id: number;
            width?: number;
            height?: number;
            params?: { left?: number; top?: number; scaleX?: number; scaleY?: number };
          }>;
        }>;
      }
    | undefined;
  if (!modelData?.uv_list) return;
  const uv = modelData.uv_list.find(
    (u) => u.face_list?.[0]?.view_id === viewId,
  );
  if (!uv?.name) return;
  try {
    const designColorCode = (product.userData as any)
      ?.hicustomModelColorCode as string[] | undefined;
    // 跟 mountHicustom3d 一致：designColorCode → tone；allowAlpha=1 → 透明
    // （HN5V75 玻璃）；其余 → 白底
    const rp = (product.userData as any)?.hicustomModelData?.render_params;
    const allowAlpha = rp?.allowAlpha === 1 || rp?.allowAlpha === '1';
    const compositeRgb =
      designColorCode && designColorCode.length >= 3
        ? designColorCode
        : allowAlpha
        ? undefined
        : ['255', '255', '255'];
    const composited = await compositeDesignOnColor(
      designUrl,
      compositeRgb,
      2048,
      uv.face_list?.[0],
    );
    const tex = await loadTextureFromSrc(composited);
    applyTextureToMaterial(root, uv.name, tex);
  } catch (e) {
    console.warn('[hicustom] updateHicustom3dFaceTexture failed', viewId, e);
  }
};
